import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Shield, Users, LogOut, UserPlus, Search, X, Menu,
  Trash2, Edit3, BarChart3, UserRound, RefreshCw, Loader2,
  Server, AppWindow, Settings, Activity, Database, AlertCircle, AlertTriangle, Cpu, LayoutTemplate, Plus,
  ShieldAlert, Globe, Terminal, GitBranch, CreditCard, ScrollText, Megaphone, Calendar, Filter, RotateCcw,
  ChevronRight, ChevronDown, ChevronLeft, User, Pause, Play, Square, CheckSquare, Check, Download, FileText, Printer, TrendingUp, UserMinus, TrendingDown, ShieldCheck, Eye, EyeOff, Copy, XCircle, Clock, Sliders, FileCode, Code, Folder, Key, Link
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import api from '../services/api';
import SystemUsersSection from '../components/superadmin/SystemUsersSection';
import SystemLogsSection from '../components/superadmin/SystemLogsSection';
import InfrastructureSection from '../components/superadmin/InfrastructureSection';
import RevenueBillingSection from '../components/superadmin/RevenueBillingSection';
import PricingPackagesSection from '../components/superadmin/PricingPackagesSection';
import TeamMemberDetailsSection from '../components/superadmin/TeamMemberDetailsSection';
import AdminTeamMembersSection from '../components/superadmin/AdminTeamMembersSection';
import superAdminApi from '../services/superAdminApi';
import useDashboardRequests from '../hooks/useDashboardRequests';
import '../styles/super-admin.css';

const formatBytesValue = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(k)), sizes.length - 1);
  return `${Math.round((value / Math.pow(k, index)) * 100) / 100} ${sizes[index]}`;
};

const renderPaginationControls = (currentPage, totalPages, totalItems, pageSize, onPageChange, label = 'entries') => {
  if (totalPages <= 1) return null;
  const startItem = ((currentPage - 1) * pageSize) + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', padding: '0.25rem 0.25rem 0' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
        Showing {startItem} to {endItem} of {totalItems} {label}
      </div>
      <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
        <button
          className="sa-action-btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          style={{ opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
        >
          Previous
        </button>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-main)', fontWeight: 600, padding: '0 0.25rem' }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="sa-action-btn"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          style={{ opacity: currentPage === totalPages ? 0.5 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

function SuperAdminDashboard({ user, onLogout }) {
  const SIDEBAR_CONFIG = [
    {
      label: 'Core',
      items: [
        { id: 'overview', label: 'Dashboard', icon: BarChart3 },
        { id: 'serverlogs', label: 'Realtime Logs', icon: Terminal },
        { id: 'users', label: 'User Management', icon: Users, badge: true }
      ]
    },
    {
      label: 'Infrastructure',
      items: [
        { id: 'infrastructure', label: 'Resource Control', icon: Server },
        { id: 'revenue', label: 'Revenue & Billing', icon: CreditCard },
        { id: 'applications', label: 'Global Deployments', icon: AppWindow },
        { id: 'domains', label: 'Custom Domains', icon: Globe },
      ]
    },
    {
      label: 'Health & Security',
      items: [
        { id: 'health', label: 'System Health', icon: ShieldCheck },
        { id: 'security', label: 'Security & WAF', icon: ShieldAlert }
      ]
    },
    {
      label: 'System & Audit',
      items: [
        { id: 'audit', label: 'Audit Logs', icon: ScrollText },
        { id: 'gitconnections', label: 'Git Providers', icon: GitBranch },
        { id: 'announcements', label: 'Broadcasts', icon: Megaphone },
        { id: 'support', label: 'Support Tickets', icon: FileText }
      ]
    },
    {
      label: 'Billing & Config',
      items: [
        { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
        { id: 'packages', label: 'Pricing Packages', icon: LayoutTemplate },
        { id: 'settings', label: 'Configuration', icon: Settings }
      ]
    }
  ];
  const [activeSection, setActiveSection] = useState(() => window.location.hash === '#logs' ? 'serverlogs' : 'overview');
  useEffect(() => {
    if (activeSection === 'serverlogs') {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#logs`);
    } else if (window.location.hash === '#logs') {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [activeSection]);
  useEffect(() => {
    const onHashChange = () => {
      if (window.location.hash === '#logs') setActiveSection('serverlogs');
      else setActiveSection(current => current === 'serverlogs' ? 'overview' : current);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const [expandedAiGroups, setExpandedAiGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const { run: runDashboardRequest, errors: dashboardErrors, pending: pendingDashboardRequests, mounted: dashboardMounted } = useDashboardRequests();
  const dashboardLoadVersion = useRef(0);
  const [systemStats, setSystemStats] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [userResourceUsage, setUserResourceUsage] = useState(null);
  const [selectedUserResourceDetails, setSelectedUserResourceDetails] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [selectedTeamMemberParent, setSelectedTeamMemberParent] = useState(null);
  const [selectedAdminForMembers, setSelectedAdminForMembers] = useState(null);
  const [isLoadingUserResource, setIsLoadingUserResource] = useState(false);
  const [allDeployments, setAllDeployments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogTotal, setAuditLogTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditFilterSeverity, setAuditFilterSeverity] = useState('');
  const [auditFilterAction, setAuditFilterAction] = useState('');
  const [auditFilterDate, setAuditFilterDate] = useState('');
  const [auditDateFocused, setAuditDateFocused] = useState(false);
  const [auditSearchQuery, setAuditSearchQuery] = useState('');
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [auditSummary, setAuditSummary] = useState(null);
  const [isAuditSummaryLoading, setIsAuditSummaryLoading] = useState(false);

  // New Features State
  const [systemErrors, setSystemErrors] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [dbStats, setDbStats] = useState(null);
  const [globalDomains, setGlobalDomains] = useState([]);

  const [gitConnections, setGitConnections] = useState([]);
  const [gitConnectionsSummary, setGitConnectionsSummary] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [lifecycleSettings, setLifecycleSettings] = useState({ gracePeriodDays: 7, retentionPeriodDays: 30, notificationFrequencyDays: 1 });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [visibleAiKeys, setVisibleAiKeys] = useState({});
  const [copiedAiKeyIdx, setCopiedAiKeyIdx] = useState(null);
  const [testingAiEndpointIdx, setTestingAiEndpointIdx] = useState(null);
  const [aiEndpointFilter, setAiEndpointFilter] = useState('All');

  const handleTestAiEndpoint = async (idx, ep) => {
    if (!ep.url || !ep.key || !ep.model) {
      showStatus('Error: URL, API Key, and Model are required to test.', 'error');
      return;
    }
    setTestingAiEndpointIdx(idx);
    try {
      const response = await fetch('/api/super-admin/ai-endpoints/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url: ep.url, key: ep.key, model: ep.model })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showStatus(`Test successful! Response: ${data.result}`, 'success');
      } else {
        showStatus(data.error || 'Test failed', 'error');
      }
    } catch (err) {
      showStatus('Connection failed: ' + err.message, 'error');
    } finally {
      setTestingAiEndpointIdx(null);
    }
  };

  // Additional Section Pagination States
  const [supportTicketsPage, setSupportTicketsPage] = useState(1);
  const [ticketSearchQuery, setTicketSearchQuery] = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState('ALL');
  const [selectedTicketModal, setSelectedTicketModal] = useState(null);
  const [systemErrorsPage, setSystemErrorsPage] = useState(1);
  const [errorSearchQuery, setErrorSearchQuery] = useState('');
  const [errorFilterType, setErrorFilterType] = useState('ALL');
  const [selectedErrorModal, setSelectedErrorModal] = useState(null);
  const [globalDomainsPage, setGlobalDomainsPage] = useState(1);
  const [gitConnectionsPage, setGitConnectionsPage] = useState(1);
  const [manualUsersPage, setManualUsersPage] = useState(1);
  const [activeGitTab, setActiveGitTab] = useState('git');
  const [gitConnectionsSearch, setGitConnectionsSearch] = useState('');
  const [gitProviderFilter, setGitProviderFilter] = useState('all');
  const [manualStatusFilter, setManualStatusFilter] = useState('all');
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [bannedIPsPage, setBannedIPsPage] = useState(1);
  const saItemsPerPage = 10;

  // Status toast
  const [status, setStatus] = useState(null);
  const [statusVisible, setStatusVisible] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const statusTimer = useRef(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [userSort, setUserSort] = useState('username');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [viewingUser, setViewingUser] = useState(null);
  const [customLimits, setCustomLimits] = useState({ maxDeployments: '', memoryLimitMB: '', storageLimitGB: '' });
  const [isSavingLimits, setIsSavingLimits] = useState(false);
  const [editingLimitsForUser, setEditingLimitsForUser] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [accountType, setAccountType] = useState('customer');
  const [newRole, setNewRole] = useState('admin');
  const [newSubscriptionStatus, setNewSubscriptionStatus] = useState('active');
  const [packageMode, setPackageMode] = useState('package');
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('pro');
  const [customLimitsForm, setCustomLimitsForm] = useState({
    maxDeployments: 25,
    maxTeamMembers: 5,
    memoryLimitGB: 8,
    cpuLimit: 4,
    storageLimitGB: 20,
    cicdEnabled: true,
    backupsEnabled: true,
    planName: 'Custom Enterprise Plan'
  });
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-sync role based on account type and plan limits
  useEffect(() => {
    if (accountType === 'staff') {
      setNewRole('superadmin');
      return;
    }
    if (packageMode === 'custom') {
      const members = Number(customLimitsForm?.maxTeamMembers) || 0;
      setNewRole(members > 0 ? 'admin' : 'developer');
    } else {
      const currentPkg = (packages || []).find(p => (p.tierId === selectedPackage || p.id === selectedPackage));
      const members = currentPkg?.limits?.maxTeamMembers ?? (selectedPackage === 'hobby' ? 0 : 5);
      const isSolo = members === 0 || selectedPackage === 'hobby';
      setNewRole(isSolo ? 'developer' : 'admin');
    }
  }, [accountType, packageMode, selectedPackage, customLimitsForm?.maxTeamMembers, packages]);

  // HCI Emergency Loaders
  const [isPruningImages, setIsPruningImages] = useState(false);
  const [isKillingZombies, setIsKillingZombies] = useState(false);
  const [isFullPruning, setIsFullPruning] = useState(false);
  const [isClearingTemp, setIsClearingTemp] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [isBuilderPruning, setIsBuilderPruning] = useState(false);

  // Dashboard Expansion States
  const [hierarchyExpanded, setHierarchyExpanded] = useState(false);
  const [totalUsersExpanded, setTotalUsersExpanded] = useState(false);
  const [developersExpanded, setDevelopersExpanded] = useState(false);
  const [deploymentsExpanded, setDeploymentsExpanded] = useState(false);
  const [activeSubsExpanded, setActiveSubsExpanded] = useState(false);
  const [mrrExpanded, setMrrExpanded] = useState(false);
  const [churnExpanded, setChurnExpanded] = useState(false);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState(null);
  // Apps states
  const [appSearch, setAppSearch] = useState("");
  const [appPage, setAppPage] = useState(1);
  const [appsPerPage] = useState(10);
  const [viewingEnvApp, setViewingEnvApp] = useState(null);
  const [envData, setEnvData] = useState("");
  const [isEnvLoading, setIsEnvLoading] = useState(false);
  const [deploymentsTab, setDeploymentsTab] = useState('all');

  // Subs states
  const [viewingInvoicesUser, setViewingInvoicesUser] = useState(null);
  const [invoicesData, setInvoicesData] = useState([]);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(false);
  const [managingPlanUser, setManagingPlanUser] = useState(null);
  const [planUpgradeLoading, setPlanUpgradeLoading] = useState(false);


  // Export Dropdown States
  const [showSubscribersExportMenu, setShowSubscribersExportMenu] = useState(false);
  const [showMrrExportMenu, setShowMrrExportMenu] = useState(false);
  const [showDeploymentsExportMenu, setShowDeploymentsExportMenu] = useState(false);
  const [showSubscriberGraphs, setShowSubscriberGraphs] = useState(false);

  // Revenue Analytics States
  const [revenueTimeRange, setRevenueTimeRange] = useState('30d');
  const [revenueTypeFilter, setRevenueTypeFilter] = useState('all');

  // Pagination & Search States for Analytics Pages
  const [subscribersPage, setSubscribersPage] = useState(1);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [subscribersSearch, setSubscribersSearch] = useState('');
  const [transactionsSearch, setTransactionsSearch] = useState('');
  const pageSize = 5;

  const toggleHierarchy = () => {
    const nextState = !hierarchyExpanded;
    setHierarchyExpanded(nextState);
    setTotalUsersExpanded(false);
    setDevelopersExpanded(false);
    setDeploymentsExpanded(false);
    setActiveSubsExpanded(false);
    setMrrExpanded(false);
    setChurnExpanded(false);

    setHierarchySearch('');
    setUserFilter('');
    setSelectedUserIds(new Set());

    if (nextState) {
      const roleStr = 'system_admin';
      setSelectedRoleFilter(roleStr);
      loadUsers(roleStr);
    } else {
      setSelectedRoleFilter(null);
      loadUsers();
    }
  };

  const toggleTotalUsers = () => {
    const nextState = !totalUsersExpanded;
    setTotalUsersExpanded(nextState);
    setHierarchyExpanded(false);
    setDevelopersExpanded(false);
    setDeploymentsExpanded(false);
    setActiveSubsExpanded(false);
    setMrrExpanded(false);
    setChurnExpanded(false);

    setHierarchySearch('');
    setUserFilter('');
    setSelectedUserIds(new Set());

    setSelectedRoleFilter(null);
    loadUsers();
  };

  const toggleDevelopers = () => {
    const nextState = !developersExpanded;
    setDevelopersExpanded(nextState);
    setHierarchyExpanded(false);
    setTotalUsersExpanded(false);
    setDeploymentsExpanded(false);
    setActiveSubsExpanded(false);
    setMrrExpanded(false);
    setChurnExpanded(false);

    setHierarchySearch('');
    setUserFilter('');
    setSelectedUserIds(new Set());

    if (nextState) {
      const roleStr = 'developer';
      setSelectedRoleFilter(roleStr);
      loadUsers(roleStr);
    } else {
      setSelectedRoleFilter(null);
      loadUsers();
    }
  };

  const toggleDeployments = () => {
    const nextState = !deploymentsExpanded;
    setDeploymentsExpanded(nextState);
    setHierarchyExpanded(false);
    setTotalUsersExpanded(false);
    setDevelopersExpanded(false);
    setActiveSubsExpanded(false);
    setMrrExpanded(false);
    setChurnExpanded(false);

    setHierarchySearch('');
    setUserFilter('');
    setSelectedUserIds(new Set());

    setSelectedRoleFilter(null);
  };

  const toggleActiveSubs = () => {
    const nextState = !activeSubsExpanded;
    setActiveSubsExpanded(nextState);
    setMrrExpanded(false);
    setChurnExpanded(false);
    setHierarchyExpanded(false);
    setTotalUsersExpanded(false);
    setDevelopersExpanded(false);
    setDeploymentsExpanded(false);
    setHierarchySearch('');
    setUserFilter('');
    setSelectedUserIds(new Set());
    setSelectedRoleFilter(null);
    loadUsers();
  };

  const toggleMrr = () => {
    const nextState = !mrrExpanded;
    setMrrExpanded(nextState);
    setActiveSubsExpanded(false);
    setChurnExpanded(false);
    setHierarchyExpanded(false);
    setTotalUsersExpanded(false);
    setDevelopersExpanded(false);
    setDeploymentsExpanded(false);
    setHierarchySearch('');
    setUserFilter('');
    setSelectedUserIds(new Set());
    setSelectedRoleFilter(null);
    loadUsers();
  };

  const toggleChurn = () => {
    const nextState = !churnExpanded;
    setChurnExpanded(nextState);
    setActiveSubsExpanded(false);
    setMrrExpanded(false);
    setHierarchyExpanded(false);
    setTotalUsersExpanded(false);
    setDevelopersExpanded(false);
    setDeploymentsExpanded(false);
    setHierarchySearch('');
    setUserFilter('');
    setSelectedUserIds(new Set());
    setSelectedRoleFilter(null);
    loadUsers();
  };
  const [expandedAdmins, setExpandedAdmins] = useState({});
  const [hierarchySearch, setHierarchySearch] = useState('');

  const [isBulkActioning, setIsBulkActioning] = useState(false);

  const toggleAdminExpansion = (username) => {
    setExpandedAdmins(prev => ({ ...prev, [username]: !prev[username] }));
  };

  // Packages
  const handlePackageChange = (index, field, value) => {
    const updated = [...packages];
    updated[index][field] = value;
    setPackages(updated);
  };

  const handleFeatureChange = (index, field, value) => {
    const updated = [...packages];
    updated[index][field] = value.split('\n').filter(s => s.trim());
    setPackages(updated);
  };

  const handleLimitChange = (index, field, value) => {
    const updated = [...packages];
    if (!updated[index].limits) updated[index].limits = {};
    updated[index].limits[field] = value;
    setPackages(updated);
  };

  const handleAddPackage = () => {
    setPackages([...packages, {
      id: `custom-${Date.now()}`,
      tier: 'New Tier',
      price: '0',
      desc: '',
      features: [],
      disabledFeatures: []
    }]);
  };

  const handleRemovePackage = (index) => {
    const updated = [...packages];
    updated.splice(index, 1);
    setPackages(updated);
  };

  // Status Message
  const showStatus = useCallback((message, type = 'info') => {
    if (statusTimer.current) clearTimeout(statusTimer.current);
    setStatus({ message, type });
    setStatusVisible(true);
    statusTimer.current = setTimeout(() => {
      setStatusVisible(false);
      setTimeout(() => setStatus(null), 300);
    }, type === 'success' ? 2000 : 4000);
  }, []);

  const loadUsers = useCallback((roleParam) => runDashboardRequest(
    'users', 'Users', signal => superAdminApi.getSystemUsers(roleParam, { signal }),
    data => setUsers(data.users || [])
  ), [runDashboardRequest]);

  const loadPackages = useCallback(() => runDashboardRequest(
    'packages', 'Packages', signal => api.getPackages({ signal }),
    data => setPackages(data.packages || [])
  ), [runDashboardRequest]);


  const handleAppAction = async (appName, action) => {
    if (!window.confirm(`Are you sure you want to ${action} deployment "${appName}"?`)) return;
    try {
      await api.post(`/super-admin/deployments/${appName}/${action}`);
      showStatus(`Successfully executed ${action} on ${appName}`, 'success');
      loadSystemData();
    } catch (err) {
      showStatus(err.message, 'error');
    }
  };

  const handleInspectEnv = async (appName) => {
    setViewingEnvApp(appName);
    setIsEnvLoading(true);
    try {
      const res = await api.get(`/super-admin/deployments/${appName}/env`);
      setEnvData(res.env || '');
    } catch (err) {
      showStatus(err.message, 'error');
      setViewingEnvApp(null);
    }
    setIsEnvLoading(false);
  };

  const handleCancelSubscription = async (username) => {
    if (!window.confirm(`Force cancel subscription for ${username}?`)) return;
    try {
      await api.post(`/super-admin/users/${username}/cancel-subscription`);
      showStatus('Subscription canceled.', 'success');
      loadAll();
    } catch (err) {
      showStatus(err.message, 'error');
    }
  };

  const handleUpgradePlan = async (e) => {
    e.preventDefault();
    setPlanUpgradeLoading(true);
    try {
      await api.post(`/super-admin/users/${managingPlanUser.username}/upgrade-plan`);
      showStatus('Plan upgraded successfully.', 'success');
      setManagingPlanUser(null);
      loadAll();
    } catch (err) {
      showStatus(err.message, 'error');
    }
    setPlanUpgradeLoading(false);
  };

  const handleViewInvoices = async (username) => {
    setViewingInvoicesUser(username);
    setIsInvoicesLoading(true);
    try {
      const res = await api.get(`/super-admin/users/${username}/invoices`);
      setInvoicesData(res.invoices || []);
    } catch (err) {
      showStatus(err.message, 'error');
    }
    setIsInvoicesLoading(false);
  };

  const loadSystemData = useCallback(() => {
    // Apply each result as it arrives; a slow section must not hold back the others.
    const tasks = [
      ['stats', 'System statistics', 'getStats', setSystemStats, () => setSystemStats(previous => previous || { error: true })],
      ['health', 'System health', 'getSystemHealth', setSystemHealth, () => setSystemHealth(previous => previous || { error: true })],
      ['deployments', 'Deployments', 'getAllDeployments', data => setAllDeployments(data.deployments || [])],
      ['database', 'Database statistics', 'getDatabaseStats', setDbStats, () => setDbStats(previous => previous || { error: true })],
      ['domains', 'Domains', 'getGlobalDomains', data => setGlobalDomains(data.domains || [])],
      ['git', 'Git connections', 'getGitConnections', data => { setGitConnections(data.connections || []); setGitConnectionsSummary(data); }],
      ['subscriptions', 'Subscriptions', 'getSubscriptions', setSubscriptions, () => setSubscriptions(previous => previous || { error: true })],
      ['errors', 'System errors', 'getSystemErrors', data => setSystemErrors(data.errors || [])],
      ['tickets', 'Support tickets', 'getSupportTickets', data => setSupportTickets(data.tickets || [])],
      ['usage', 'User resource usage', 'getUserResourceUsage', data => setUserResourceUsage(data.usage || []), () => setUserResourceUsage(previous => previous || [])],
    ];
    return Promise.allSettled(tasks.map(([key, label, method, apply, onFailure]) => runDashboardRequest(
      key, label, signal => superAdminApi[method]({ signal }), apply, onFailure
    )));
  }, [runDashboardRequest]);

  const [announcementMsg, setAnnouncementMsg] = useState('');
  const [announcementActive, setAnnouncementActive] = useState(false);

  const [securityData, setSecurityData] = useState({ bannedIPs: [] });
  const [newBanIp, setNewBanIp] = useState('');

  const loadSecurityData = useCallback(async () => {
    try {
      const data = await api.get('/super-admin/security');
      setSecurityData(data);
    } catch (err) {
      // ignore
    }
  }, []);

  const loadDbStats = useCallback(async () => {
    try {
      const data = await superAdminApi.getDatabaseStats();
      if (data && data.tables) {
        setDbStats(data);
      }
    } catch (err) {
      // ignore
    }
  }, []);

  const addBanIp = async () => {
    if (!newBanIp.trim()) return;
    try {
      const data = await api.post('/super-admin/security/ban', { ip: newBanIp.trim() });
      setSecurityData(data);
      setNewBanIp('');
      showStatus('IP added to ban list', 'success');
    } catch (err) {
      showStatus('Failed to ban IP', 'error');
    }
  };

  const removeBanIp = async (ip) => {
    try {
      const data = await api.delete(`/super-admin/security/ban/${encodeURIComponent(ip)}`);
      setSecurityData(data);
      showStatus('IP removed from ban list', 'success');
    } catch (err) {
      showStatus('Failed to remove ban', 'error');
    }
  };

  const loadAnnouncement = useCallback(async () => {
    try {
      const data = await api.get('/announcements');
      setAnnouncementMsg(data.message || '');
      setAnnouncementActive(data.active || false);
    } catch (err) {
      // ignore
    }
  }, []);

  const saveAnnouncement = async () => {
    try {
      await api.post('/super-admin/announcements', { message: announcementMsg, active: announcementActive });
      showStatus('Announcement updated successfully', 'success');
    } catch (err) {
      showStatus('Failed to update announcement', 'error');
    }
  };

  const [financialStats, setFinancialStats] = useState({
    activeSubscribers: 0,
    estimatedMRR: 0,
    churnRate: 0,
    subscribersList: [],
    revenueHistory: [],
    chartData: []
  });

  const loadFinancialStats = useCallback(async () => {
    try {
      const data = await api.get(`/super-admin/financial-stats?timeRange=${revenueTimeRange}&revenueType=${revenueTypeFilter}`);
      setFinancialStats(data);
    } catch (err) {
      // ignore
    }
  }, [revenueTimeRange, revenueTypeFilter]);


  const loadSystemSettings = useCallback(async () => {
    try {
      const data = await superAdminApi.getSystemSettings();
      setLifecycleSettings({
        ...data,
        aiEndpoints: Array.isArray(data.aiEndpoints) ? data.aiEndpoints : []
      });
    } catch (e) {
      showStatus('Error fetching system settings', 'error');
    }
  }, []);

  const loadAuditLogs = useCallback(async () => {
    setIsAuditLoading(true);
    try {
      const data = await superAdminApi.getAuditLogs({
        page: 1,
        limit: 500,
        severity: auditFilterSeverity,
        query: auditSearchQuery
      });
      setAuditLogs(data.logs || []);
      setAuditLogTotal(data.pagination?.total || (data.logs || []).length);
    } catch (e) {
      showStatus('Failed to load audit logs', 'error');
    }
    setIsAuditLoading(false);
  }, [auditFilterSeverity, auditSearchQuery, showStatus]);

  const loadAuditSummary = useCallback(async () => {
    setIsAuditSummaryLoading(true);
    try {
      const data = await superAdminApi.getAuditSummary();
      setAuditSummary(data);
    } catch (e) {
      console.warn('Failed to load audit summary:', e);
    }
    setIsAuditSummaryLoading(false);
  }, []);

  const loadAll = useCallback(async () => {
    if (!dashboardMounted.current) return;
    const version = ++dashboardLoadVersion.current;
    setLoading(true);
    try {
      await Promise.allSettled([loadUsers(selectedRoleFilter), loadPackages(), loadSystemData()]);
    } finally {
      if (dashboardMounted.current && version === dashboardLoadVersion.current) setLoading(false);
    }
  }, [loadUsers, selectedRoleFilter, loadPackages, loadSystemData, dashboardMounted]);

  useEffect(() => {
    if (activeSection === 'audit') {
      loadAuditLogs();
      loadAuditSummary();

    } else if (activeSection === 'announcements') {
      loadAnnouncement();
    } else if (activeSection === 'overview' || activeSection === 'revenue' || activeSection === 'active_subscribers_detail' || activeSection === 'mrr_details' || activeSection === 'churn_details') {
      loadFinancialStats();
    } else if (activeSection === 'security') {
      loadSecurityData();
    } else if (activeSection === 'dbstats') {
      loadDbStats();
    } else if (activeSection === 'settings') {
      loadSystemSettings();
    }
  }, [activeSection, auditPage, auditFilterSeverity, auditSearchQuery, loadAuditLogs, loadAnnouncement, loadFinancialStats, loadSecurityData, loadDbStats, loadSystemSettings]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // User CRUD
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const isCustomPlan = accountType === 'customer' && packageMode === 'custom';
      const customPayload = isCustomPlan ? {
        maxDeployments: Number(customLimitsForm.maxDeployments) || 25,
        maxTeamMembers: Number(customLimitsForm.maxTeamMembers) || 5,
        memoryLimitMB: (Number(customLimitsForm.memoryLimitGB) || 8) * 1024,
        cpuLimit: Number(customLimitsForm.cpuLimit) || 4,
        storageLimitGB: Number(customLimitsForm.storageLimitGB) || 20,
        cicdEnabled: Boolean(customLimitsForm.cicdEnabled),
        backupsEnabled: Boolean(customLimitsForm.backupsEnabled),
        planName: customLimitsForm.planName || 'Custom Enterprise Plan'
      } : null;

      const effectiveTier = accountType === 'staff'
        ? 'enterprise'
        : (packageMode === 'custom' ? 'custom' : selectedPackage);

      if (editingUser) {
        await api.updateAdminUser(editingUser.username, {
          newUsername: newUsername.trim(),
          fullName: newFullName ? newFullName.trim() : undefined,
          email: newEmail ? newEmail.trim() : undefined,
          password: newPassword || undefined,
          role: newRole,
          packageTier: effectiveTier,
          subscriptionStatus: newSubscriptionStatus,
          customLimits: customPayload
        });
        if (newSubscriptionStatus !== editingUser.subscriptionStatus) {
          await api.patch(`/super-admin/users/${editingUser.username}/subscription`, { status: newSubscriptionStatus }).catch(() => {});
        }
        if (isCustomPlan && customPayload) {
          await superAdminApi.updateUserLimits(editingUser.username, customPayload).catch(() => {});
        } else if (accountType === 'customer' && packageMode === 'package' && selectedPackage) {
          await api.patch(`/super-admin/users/${editingUser.username}/subscription`, { packageTier: selectedPackage }).catch(() => {});
        }
        showStatus('User updated successfully', 'success');
      } else {
        if (!newPassword) {
          showStatus('Password is required for new user', 'error');
          setIsSubmitting(false);
          return;
        }
        if (!newEmail) {
          showStatus('Email is required for new user', 'error');
          setIsSubmitting(false);
          return;
        }
        if (users && users.some(u => u.username?.toLowerCase() === newUsername.trim().toLowerCase())) {
          showStatus(`Username "${newUsername.trim()}" is already taken. Please choose a different username.`, 'error');
          setIsSubmitting(false);
          return;
        }
        await api.createAdminUser({
          username: newUsername.trim(),
          email: newEmail.trim(),
          fullName: newFullName ? newFullName.trim() : undefined,
          password: newPassword,
          role: newRole,
          packageTier: effectiveTier,
          subscriptionStatus: newSubscriptionStatus,
          customLimits: customPayload,
          sendWelcomeEmail: Boolean(sendWelcomeEmail)
        });
        showStatus('User created successfully', 'success');
      }
      clearUserForm();
      await loadAll();
    } catch (err) {
      showStatus(err.message, 'error');
    }
    setIsSubmitting(false);
  };

  const handleUserDelete = async (u, mode = 'soft') => {
    if (u.username === user?.username) {
      showStatus('Cannot delete your own account', 'error');
      return;
    }

    const confirmMsg = mode === 'hard'
      ? `PERMANENTLY PURGE user "${u.username}"?\n\nThis will permanently delete the user record and all data from the database. This action CANNOT be undone.`
      : `Are you sure you want to delete user "${u.username}"?\n\nThis will tear down their containers/deployments and revoke access, while PRESERVING their billing history.`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      await api.deleteAdminUser(u.username, mode);
      showStatus(`User ${u.username} ${mode === 'soft' ? 'deleted (deployments torn down, billing history preserved)' : 'permanently purged'} successfully`, 'success');
      await loadAll();
    } catch (err) {
      showStatus(err.message, 'error');
    }
  };

  const handleDeleteDeployment = async (dep) => {
    if (!window.confirm(`Delete deployment "${dep.appName}"? This action cannot be undone.`)) return;
    try {
      await api.deleteApp(dep.appPath);
      showStatus('Deployment deleted', 'success');
      await loadAll();
    } catch (err) {
      showStatus(err.message, 'error');
    }
  };

  // const handleActionDeployment = async (dep, action) => {
  //   if (!window.confirm(`Are you sure you want to ${action} deployment "${dep.appName}"?`)) return;
  //   try {
  //     await api.controlContainer(dep.appName, action, { owner: dep.owner });
  //     showStatus(`Deployment ${action}ed successfully`, 'success');
  //     await loadAll();
  //   } catch (err) {
  //     showStatus(err.message, 'error');
  //   }
  // };

  const handleToggleActive = async (u, e) => {
    e.stopPropagation();
    if (u.username === user?.username) {
      showStatus('Cannot suspend your own account', 'error');
      return;
    }
    const newStatus = !(u.isActive !== false); // default is true if undefined
    try {
      await api.toggleUserActive(u.username, newStatus);
      showStatus(`User ${newStatus ? 'activated' : 'suspended'}`, 'success');

      // Update selectedTeamMember state so UI reflects the change immediately
      if (selectedTeamMember && selectedTeamMember.username === u.username) {
        setSelectedTeamMember(prev => ({ ...prev, isActive: newStatus }));
      }
      if (viewingUser && viewingUser.username === u.username) {
        setViewingUser(prev => ({ ...prev, isActive: newStatus }));
      }

      await loadAll();
    } catch (err) {
      showStatus(err.message, 'error');
    }
  };





  const handleSaveLimits = async () => {
    if (!editingLimitsForUser) return;
    setIsSavingLimits(true);
    try {
      const parsedLimits = {
        maxDeployments: customLimits.maxDeployments ? parseInt(customLimits.maxDeployments) : null,
        memoryLimitMB: customLimits.memoryLimitMB ? parseInt(customLimits.memoryLimitMB) : null,
        storageLimitGB: customLimits.storageLimitGB ? parseInt(customLimits.storageLimitGB) : null,
        cpuLimit: customLimits.cpuLimit ? parseFloat(customLimits.cpuLimit) : null,
        maxDomains: customLimits.maxDomains ? parseInt(customLimits.maxDomains) : null,
        maxDatabases: customLimits.maxDatabases ? parseInt(customLimits.maxDatabases) : null,
        bandwidthLimitGB: customLimits.bandwidthLimitGB ? parseInt(customLimits.bandwidthLimitGB) : null,
      };
      await api.put(`/super-admin/users/${editingLimitsForUser.username}/limits`, { customLimits: parsedLimits });
      if (typeof showStatus === 'function') {
        showStatus(`Limits for user ${editingLimitsForUser.username} updated successfully`, 'success');
      }
      setEditingLimitsForUser(null);
      if (typeof loadAll === 'function') {
        await loadAll();
      }
    } catch (err) {
      if (typeof showStatus === 'function') {
        showStatus(err.message || 'Error updating limits', 'error');
      }
    }
    setIsSavingLimits(false);
  };

  // const handleBulkAction = async (action) => {
  //   if (selectedUserIds.size === 0) return;
  //   if (action === 'delete' && !window.confirm(`Delete ${selectedUserIds.size} users? This cannot be undone.`)) return;
  // 
  //   setIsBulkActioning(true);
  //   try {
  //     await api.bulkUserAction(Array.from(selectedUserIds), action);
  //     showStatus(`Bulk ${action} successful`, 'success');
  //     setSelectedUserIds(new Set());
  //     await loadAll();
  //   } catch (err) {
  //     showStatus(err.message, 'error');
  //   }
  //   setIsBulkActioning(false);
  // };

  const startEditUser = (u) => {
    setEditingUser(u);
    setNewUsername(u.username || '');
    setNewFullName(u.fullName || '');
    setNewEmail(u.email || '');
    setNewSubscriptionStatus(u.subscriptionStatus || 'active');
    setNewPassword('');

    if (u.role === 'superadmin') {
      setAccountType('staff');
      setNewRole('superadmin');
    } else {
      setAccountType('customer');
      setNewRole(u.role || 'admin');
    }

    const hasCustom = u.customLimits && typeof u.customLimits === 'object' && Object.keys(u.customLimits).length > 0;
    const isCustom = u.packageTier === 'custom' || hasCustom;
    if (isCustom) {
      setPackageMode('custom');
      setCustomLimitsForm({
        maxDeployments: u.customLimits?.maxDeployments ?? 25,
        maxTeamMembers: u.customLimits?.maxTeamMembers ?? 5,
        memoryLimitGB: u.customLimits?.memoryLimitMB ? Math.round(u.customLimits.memoryLimitMB / 1024) : 8,
        cpuLimit: u.customLimits?.cpuLimit ?? 4,
        storageLimitGB: u.customLimits?.storageLimitGB ?? (u.customLimits?.storageGB ?? 20),
        cicdEnabled: u.customLimits?.cicdEnabled !== false,
        backupsEnabled: u.customLimits?.backupsEnabled !== false,
        planName: u.customLimits?.planName || 'Custom Enterprise Plan'
      });
    } else {
      setPackageMode('package');
      setSelectedPackage(u.packageTier || (u.role === 'admin' ? 'pro' : 'hobby'));
    }
    setSendWelcomeEmail(false);
    setShowUserForm(true);
  };

  const clearUserForm = () => {
    setEditingUser(null);
    setAccountType('customer');
    setNewUsername('');
    setNewFullName('');
    setNewEmail('');
    setNewPassword('');
    setNewRole('admin');
    setNewSubscriptionStatus('active');
    setPackageMode('package');
    setSelectedPackage('pro');
    setCustomLimitsForm({
      maxDeployments: 25,
      maxTeamMembers: 5,
      memoryLimitGB: 8,
      cpuLimit: 4,
      storageLimitGB: 20,
      cicdEnabled: true,
      backupsEnabled: true,
      planName: 'Custom Enterprise Plan'
    });
    setSendWelcomeEmail(true);
    setShowUserForm(false);
  };

  const filteredUsers = [...users]
    .filter(u => {
      const matchText = (u.username || '').toLowerCase().includes(userFilter.toLowerCase()) ||
        (u.email && u.email.toLowerCase().includes(userFilter.toLowerCase()));

      const matchRole = userRoleFilter === 'all' || u.role === userRoleFilter;

      const isDeleted = Boolean(u.deletedAt);

      let matchStatus = true;
      if (userStatusFilter === 'all') matchStatus = !isDeleted;
      else if (userStatusFilter === 'active') matchStatus = !isDeleted && u.isActive && (u.subscriptionStatus === 'active' || !u.subscriptionStatus);
      else if (userStatusFilter === 'suspended') matchStatus = !isDeleted && (!u.isActive || u.subscriptionStatus === 'suspended') && u.subscriptionStatus !== 'unpaid' && u.subscriptionStatus !== 'past_due' && u.subscriptionStatus !== 'failing';
      else if (userStatusFilter === 'payment_failed') matchStatus = !isDeleted && (u.subscriptionStatus === 'unpaid' || u.subscriptionStatus === 'past_due' || u.subscriptionStatus === 'failing');
      else if (userStatusFilter === 'deleted') matchStatus = isDeleted;

      return matchText && matchRole && matchStatus;
    })
    .sort((a, b) => {
      if (userSort === 'username') return (a.username || '').localeCompare(b.username || '');
      if (userSort === 'newest') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (userSort === 'lastLogin') return new Date(b.lastLogin || 0) - new Date(a.lastLogin || 0);
      return 0;
    });

  // const totalUserPages = Math.ceil(filteredUsers.length / usersPerPage) || 1;
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);

  const toggleUserSelection = (username) => {
    const newSet = new Set(selectedUserIds);
    if (newSet.has(username)) newSet.delete(username);
    else newSet.add(username);
    setSelectedUserIds(newSet);
  };

  const toggleAllSelection = () => {
    if (selectedUserIds.size === paginatedUsers.length && paginatedUsers.length > 0) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(paginatedUsers.map(u => u.username)));
    }
  };

  // Stats — prefer live API data, fall back to local user list
  const totalUsers = systemStats?.users?.total ?? users.length;
  const adminCount = (systemStats?.users?.admins !== undefined ? (systemStats.users.admins + (systemStats.users.superAdmins || 0)) : null) ?? users.filter(u => {
    const r = String(u.role || '').trim().toLowerCase();
    return r === 'admin' || r === 'superadmin' || r === 'system_admin' || r === 'systemadmin';
  }).length;
  const devCount = systemStats?.users?.developers ?? users.filter(u => u.role === 'developer').length;
  const totalDeployments = systemStats?.deployments?.total ?? 0;
  // const cpuPct = systemHealth?.cpu?.usage1mPct ?? systemStats?.system?.cpuUsagePct ?? 0;
  // const memPct = systemHealth?.memory?.usagePct ?? systemStats?.system?.memUsagePct ?? 0;
  // const diskPct = systemHealth?.disk?.usagePct ?? null;
  // const dockerSpace = systemHealth?.dockerSpace ?? null;
  // const uptime = systemHealth?.uptime ?? systemStats?.system?.uptimeSeconds ?? 0;
  // const load1m = systemHealth?.cpu?.load1m ?? 0;
  // const load5m = systemHealth?.cpu?.load5m ?? 0;
  // const load15m = systemHealth?.cpu?.load15m ?? 0;
  // const cpuCoreCount = systemHealth?.cpu?.count ?? systemStats?.system?.cpuCount ?? 1;
  // const dockerTotal = systemHealth?.docker?.totalContainers ?? 0;
  // const dockerRunning = systemHealth?.docker?.runningContainers ?? 0;

  // Format uptime nicely
  // const formatUptime = (seconds) => {
  //   if (!seconds) return '0s';
  //   const d = Math.floor(seconds / 86400);
  //   const h = Math.floor((seconds % 86400) / 3600);
  //   const m = Math.floor((seconds % 3600) / 60);
  //   if (d > 0) return `${d}d ${h}h`;
  //   if (h > 0) return `${h}h ${m}m`;
  //   return `${m}m`;
  // };
  // Fallbacks when live backend API has not yet deployed updated financial-stats endpoint
  const baseSubscribersList = (financialStats?.subscribersList && financialStats.subscribersList.length > 0)
    ? financialStats.subscribersList.filter(u => (u.userType || u.role || '').toLowerCase() !== 'superadmin')
    : users.filter(u => u.role !== 'superadmin').map(u => ({
      name: u.username || 'User',
      email: u.email || `${u.username}@devorants.com`,
      duration: '1 Month',
      planExpiration: '2026-08-24',
      userType: u.role === 'admin' ? 'Workspace Admin' : 'Developer',
      plan: u.role === 'admin' ? 'Pro' : 'Hobby',
      status: u.subscriptionStatus || (u.stripeCustomerId ? 'active' : 'partial')
    }));

  const effectiveSubscribersList = baseSubscribersList.map(u => {
    const teamSize = users.filter(member => member.createdBy === u.name).length;
    const planName = (u.plan || '').toLowerCase();
    const maxTeamMembers = planName === 'pro' ? 5 : (planName === 'enterprise' ? 20 : 0);
    const isOwner = (u.userType || '').toLowerCase().includes('admin') || (u.userType || '').toLowerCase().includes('owner');
    return {
      ...u,
      teamSize,
      maxTeamMembers,
      isOverQuota: isOwner && teamSize > maxTeamMembers
    };
  });

  const effectiveChartData = (financialStats?.chartData && financialStats.chartData.length > 0)
    ? financialStats.chartData
    : [
      { name: 'Jan', subscribers: Math.max(1, users.length - 2), revenue: Math.max(1, users.length - 2) * 20 },
      { name: 'Feb', subscribers: Math.max(1, users.length - 1), revenue: Math.max(1, users.length - 1) * 20 },
      { name: 'Mar', subscribers: users.length, revenue: users.length * 20 },
      { name: 'Apr', subscribers: users.length, revenue: users.length * 20 },
      { name: 'May', subscribers: users.length, revenue: users.length * 20 }
    ];

  const effectiveRevenueHistory = (financialStats?.revenueHistory && financialStats.revenueHistory.length > 0)
    ? financialStats.revenueHistory.filter(t => (t.username || '').toLowerCase() !== 'superadmin').map(t => {
      const rawP = t.plan || 'Pro';
      const cleanP = (rawP.toUpperCase().startsWith('CUSTOM_') || rawP.toLowerCase().includes('custom')) ? 'Pro Plan' : rawP;
      return { ...t, plan: cleanP };
    })
    : users.filter(u => u.role !== 'superadmin').map((u, i) => ({
      id: u.id || i,
      transId: `txn_live_${1000 + i}`,
      username: u.username,
      email: u.email || `${u.username}@devorants.com`,
      plan: u.role === 'admin' ? 'Pro' : 'Hobby',
      price: u.role === 'admin' ? '$10.00' : '$0.00',
      billingCycle: 'Monthly',
      date: new Date(u.createdAt || Date.now()).toISOString().split('T')[0],
      status: 'Paid'
    }));

  const filteredSubscribers = (effectiveSubscribersList || []).filter(u => {
    if (!subscribersSearch.trim()) return true;
    const q = subscribersSearch.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.plan || '').toLowerCase().includes(q) ||
      (u.status || '').toLowerCase().includes(q);
  });
  const totalSubscribersPages = Math.max(1, Math.ceil(filteredSubscribers.length / pageSize));
  const paginatedSubscribers = filteredSubscribers.slice((subscribersPage - 1) * pageSize, subscribersPage * pageSize);

  const filteredTransactions = (effectiveRevenueHistory || []).filter(t => {
    if (!transactionsSearch.trim()) return true;
    const q = transactionsSearch.toLowerCase();
    return (t.transId || '').toLowerCase().includes(q) ||
      (t.username || '').toLowerCase().includes(q) ||
      (t.email || '').toLowerCase().includes(q) ||
      (t.plan || '').toLowerCase().includes(q) ||
      (t.status || '').toLowerCase().includes(q);
  });
  const totalTransactionsPages = Math.max(1, Math.ceil(filteredTransactions.length / pageSize));
  const paginatedTransactions = filteredTransactions.slice((transactionsPage - 1) * pageSize, transactionsPage * pageSize);

  // const effectiveDbStats = (dbStats && dbStats.tables && dbStats.tables.length > 0)
  //   ? dbStats
  //   : {
  //     totalSize: '4.8 MB',
  //     tables: [
  //       { table_name: 'User', row_count: users.length || 3, total_size: '128 KB' },
  //       { table_name: 'PortAllocation', row_count: 6, total_size: '64 KB' },
  //       { table_name: 'Deployment', row_count: allDeployments.length || 6, total_size: '256 KB' },
  //       { table_name: 'Subscription', row_count: effectiveSubscribersList.length || 2, total_size: '32 KB' },
  //       { table_name: 'AuditLog', row_count: 24, total_size: '512 KB' },
  //       { table_name: 'SystemSetting', row_count: 8, total_size: '16 KB' }
  //     ]
  //   };

  const handleExportSubscribersCSV = () => {
    if (!effectiveSubscribersList || effectiveSubscribersList.length === 0) return;
    const today = new Date().toISOString().split('T')[0];
    const headerLines = [
      '# DEVORANTS PLATFORM - ACTIVE SUBSCRIBERS REPORT',
      `# Generated Date: ${today}`,
      `# Total Record Count: ${effectiveSubscribersList.length}`,
      ''
    ];
    const columns = ['Name', 'Email', 'Duration', 'Plan Expiration', 'User Type', 'Plan', 'Status'];
    const rows = effectiveSubscribersList.map(u => [
      `"${(u.name || '').replace(/"/g, '""')}"`,
      `"${(u.email || '').replace(/"/g, '""')}"`,
      `"${(u.duration || '').replace(/"/g, '""')}"`,
      `"${(u.planExpiration || '').replace(/"/g, '""')}"`,
      `"${(u.userType || '').replace(/"/g, '""')}"`,
      `"${(u.plan || '').replace(/"/g, '""')}"`,
      `"${(u.status || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = [...headerLines, columns.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Devorants_Active_Subscribers_${today}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportDeploymentsCSV = (appsList, tab = 'all') => {
    if (!appsList || appsList.length === 0) {
      showStatus('No deployment data available to export', 'info');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const tabName = String(tab).toUpperCase();
    const headerLines = [
      `# GLOBAL DEPLOYMENTS REPORT - ${tabName}`,
      `# Generated Date: ${today}`,
      `# Total Records: ${appsList.length}`,
      ''
    ];

    let columns = [];
    let rows = [];

    if (tab === 'failed') {
      columns = ['App Name', 'Owner', 'Deployment Type', 'Status', 'Failure Error Reason', 'Port'];
      rows = appsList.map(d => {
        const errorMsg = d.metadata?.error || d.metadata?.lastError || d.resourceInfo?.error || d.resourceInfo?.message || 'Container exited unexpectedly (process stopped)';
        return [
          `"${(d.appName || '').replace(/"/g, '""')}"`,
          `"${(d.owner || '').replace(/"/g, '""')}"`,
          `"${(d.type || 'docker').replace(/"/g, '""')}"`,
          `"Failed / Stopped"`,
          `"${errorMsg.replace(/"/g, '""')}"`,
          `"${d.port || '—'}"`
        ];
      });
    } else {
      columns = ['App Name', 'Owner', 'Status', 'Deployment Type', 'Port', 'CPU (%)', 'RAM'];
      rows = appsList.map(d => [
        `"${(d.appName || '').replace(/"/g, '""')}"`,
        `"${(d.owner || '').replace(/"/g, '""')}"`,
        `"${d.resourceInfo?.status === 'running' ? 'Running' : 'Stopped/Failed'}"`,
        `"${(d.type || 'docker').replace(/"/g, '""')}"`,
        `"${d.port || '—'}"`,
        `"${(d.resourceInfo?.cpuPercent || 0).toFixed(2)}%"`,
        `"${formatBytesValue(d.resourceInfo?.memUsageBytes || 0)}"`
      ]);
    }

    const csvContent = [...headerLines, columns.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Deployments_${tab}_${today}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showStatus(`Exported ${appsList.length} deployments to CSV`, 'success');
  };

  const handleExportDeploymentsJSON = (appsList, tab = 'all') => {
    if (!appsList || appsList.length === 0) {
      showStatus('No deployment data available to export', 'info');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const exportData = appsList.map(d => ({
      appName: d.appName,
      owner: d.owner,
      type: d.type || 'docker',
      port: d.port || null,
      status: d.resourceInfo?.status || 'stopped',
      cpuPercent: d.resourceInfo?.cpuPercent || 0,
      memUsageBytes: d.resourceInfo?.memUsageBytes || 0,
      errorReason: tab === 'failed' ? (d.metadata?.error || d.metadata?.lastError || d.resourceInfo?.error || d.resourceInfo?.message || 'Container process stopped') : undefined
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Deployments_${tab}_${today}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showStatus(`Exported ${appsList.length} deployments to JSON`, 'success');
  };

  const handleExportSubscribersPDF = () => {
    if (!effectiveSubscribersList || effectiveSubscribersList.length === 0) return;

    const printWindow = window.open('', '_blank');
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const tableRows = effectiveSubscribersList.map(u => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-weight: 600;">${u.name || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; color: #475569;">${u.email || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">${u.duration || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; color: #64748b;">${u.planExpiration || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">${u.userType || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;"><span style="background: #f1f5f9; padding: 3px 8px; border-radius: 12px; font-size: 12px; font-weight: 600;">${u.plan || ''}</span></td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: right;"><span style="color: #059669; font-weight: 700; font-size: 12px;">${(u.status || 'ACTIVE').toUpperCase()}</span></td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Subscribers Report - ${today}</title>
          <style>
            body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 40px; color: #1e293b; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #3b82f6; padding-bottom: 20px; margin-bottom: 30px; }
            .title { font-size: 24px; font-weight: 700; color: #0f172a; margin: 0; }
            .subtitle { font-size: 14px; color: #64748b; margin-top: 5px; }
            .meta { text-align: right; font-size: 13px; color: #64748b; }
            .summary-box { background: #f8fafc; border: 1px solid #e2e8f0; padding: 15px 20px; border-radius: 8px; margin-bottom: 30px; display: flex; gap: 40px; }
            .summary-item { font-size: 13px; }
            .summary-value { font-size: 20px; font-weight: 700; color: #3b82f6; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; text-align: left; font-size: 13px; }
            th { background: #f1f5f9; padding: 12px 10px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; border-bottom: 2px solid #cbd5e1; }
            .footer { margin-top: 50px; border-top: 1px solid #e2e8f0; padding-top: 15px; font-size: 11px; color: #94a3b8; text-align: center; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1 class="title">Devorants Platform</h1>
              <div class="subtitle">Active Subscribers Analytics Report</div>
            </div>
            <div class="meta">
              <div><strong>Generated:</strong> ${today}</div>
              <div><strong>Status:</strong> Official Report</div>
            </div>
          </div>
          <div class="summary-box">
            <div class="summary-item">
              <div>Total Active Subscribers</div>
              <div class="summary-value">${effectiveSubscribersList.length}</div>
            </div>
            <div class="summary-item">
              <div>Pro Plan Members</div>
              <div class="summary-value">${effectiveSubscribersList.filter(u => u.plan === 'Pro').length}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Duration</th>
                <th>Plan Expiration</th>
                <th>User Type</th>
                <th>Plan</th>
                <th style="text-align: right;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer">
            Generated automatically by Devorants System — All Rights Reserved
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleExportTransactionsCSV = () => {
    if (!effectiveRevenueHistory || effectiveRevenueHistory.length === 0) return;
    const today = new Date().toISOString().split('T')[0];
    const headerLines = [
      '# DEVORANTS PLATFORM - MONTHLY REVENUE & TRANSACTIONS REPORT',
      `# Generated Date: ${today}`,
      `# Total Transaction Count: ${effectiveRevenueHistory.length}`,
      ''
    ];
    const columns = ['Transaction ID', 'Username', 'Email', 'Plan', 'Amount', 'Billing Cycle', 'Date', 'Next Billing Date', 'Status'];
    const rows = effectiveRevenueHistory.map(t => [
      `"${(t.transId || '').replace(/"/g, '""')}"`,
      `"${(t.username || '').replace(/"/g, '""')}"`,
      `"${(t.email || '').replace(/"/g, '""')}"`,
      `"${(t.plan || '').replace(/"/g, '""')}"`,
      `"${(t.price || '').replace(/"/g, '""')}"`,
      `"${(t.billingCycle || '').replace(/"/g, '""')}"`,
      `"${(t.date || '').replace(/"/g, '""')}"`,
      `"${(t.nextBillingDate || (t.date ? new Date(new Date(t.date).getTime() + 30 * 86400000).toLocaleDateString() : 'N/A')).replace(/"/g, '""')}"`,
      `"${(t.status || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = [...headerLines, columns.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Devorants_MRR_Transactions_${today}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportTransactionsPDF = () => {
    if (!effectiveRevenueHistory || effectiveRevenueHistory.length === 0) return;

    const printWindow = window.open('', '_blank');
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const tableRows = effectiveRevenueHistory.map(t => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-family: monospace; font-size: 12px;">${t.transId || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-weight: 600;">${t.username || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; color: #475569;">${t.email || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;"><span style="background: #f1f5f9; padding: 3px 8px; border-radius: 12px; font-size: 12px; font-weight: 600;">${t.plan || ''}</span></td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-weight: 700; color: #0f172a;">${t.price || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; color: #64748b;">${t.billingCycle || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; color: #64748b;">${t.date || ''}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; color: #64748b;">${t.nextBillingDate || (t.date ? new Date(new Date(t.date).getTime() + 30 * 86400000).toLocaleDateString() : 'N/A')}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: right;"><span style="color: #059669; font-weight: 700; font-size: 12px;">${(t.status || 'PAID').toUpperCase()}</span></td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>MRR & Transactions Report - ${today}</title>
          <style>
            body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 40px; color: #1e293b; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #3b82f6; padding-bottom: 20px; margin-bottom: 30px; }
            .title { font-size: 24px; font-weight: 700; color: #0f172a; margin: 0; }
            .subtitle { font-size: 14px; color: #64748b; margin-top: 5px; }
            .meta { text-align: right; font-size: 13px; color: #64748b; }
            .summary-box { background: #f8fafc; border: 1px solid #e2e8f0; padding: 15px 20px; border-radius: 8px; margin-bottom: 30px; display: flex; gap: 40px; }
            .summary-item { font-size: 13px; }
            .summary-value { font-size: 20px; font-weight: 700; color: #3b82f6; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; text-align: left; font-size: 13px; }
            th { background: #f1f5f9; padding: 12px 10px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; border-bottom: 2px solid #cbd5e1; }
            .footer { margin-top: 50px; border-top: 1px solid #e2e8f0; padding-top: 15px; font-size: 11px; color: #94a3b8; text-align: center; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1 class="title">Devorants Platform</h1>
              <div class="subtitle">Monthly Recurring Revenue & Financial Audit Report</div>
            </div>
            <div class="meta">
              <div><strong>Generated:</strong> ${today}</div>
              <div><strong>Status:</strong> Official Financial Statement</div>
            </div>
          </div>
          <div class="summary-box">
            <div class="summary-item">
              <div>Total Transactions</div>
              <div class="summary-value">${effectiveRevenueHistory.length}</div>
            </div>
            <div class="summary-item">
              <div>Estimated Monthly Revenue</div>
              <div class="summary-value">$${financialStats?.estimatedMRR || (effectiveRevenueHistory.length * 20)}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Username</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Cycle</th>
                <th>Date</th>
                <th style="text-align: right;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer">
            Generated automatically by Devorants System — All Rights Reserved
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const getBreadcrumbTitle = () => {
    if (activeSection === 'user_allocations' && selectedUserResourceDetails) {
      return `${selectedUserResourceDetails.username}'s Allocations`;
    }
    for (const section of SIDEBAR_CONFIG) {
      const found = section.items.find(i => i.id === activeSection);
      if (found) return found.label;
    }
    return 'Dashboard';
  };

  return (
    <div className="container">
      {isMobileOpen && (
        <div className="mobile-menu-backdrop" onClick={() => setIsMobileOpen(false)} />
      )}
      <main className="app-main-layout">
        {/* Sidebar */}
        <aside className={`app-sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-brand-wrapper">
            <div className="sidebar-brand">
              <div className="app-brand-mark" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' }}>
                <Shield size={20} strokeWidth={2.2} color="#fff" />
              </div>
              <div className="sidebar-brand-text">
                <h1 className="app-title">Super Admin</h1>
                <div className="app-subtitle">System Control Panel</div>
              </div>
            </div>
            <button className="mobile-menu-toggle" onClick={() => setIsMobileOpen(false)} title="Close Menu">
              <X size={18} />
            </button>
          </div>

          <div className="sidebar-nav-area">
            <nav className="main-nav-container">
              <div className="main-nav">
                {SIDEBAR_CONFIG.map((section, idx) => (
                  <div className="nav-section" key={idx}>
                    <div className="nav-section-label">{section.label}</div>
                    {section.items.map(item => {
                      const Icon = item.icon;
                      return (
                        <div key={item.id} className={`nav-item-wrapper ${activeSection === item.id ? 'active' : ''}`}>
                          <button className={`nav-item ${activeSection === item.id ? 'active' : ''}`} aria-current={activeSection === item.id ? 'page' : undefined} onClick={() => { setActiveSection(item.id); setIsMobileOpen(false); }}>
                            <span className="nav-icon"><Icon size={18} strokeWidth={2.0} /></span>
                            <span className="nav-label">{item.label}</span>
                            {item.badge && <span className="nav-badge">{totalUsers}</span>}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </nav>
          </div>
        </aside>

        {/* Main Content Area */}
        <section className="app-content-area">
          <header className="app-header">
            <div className="app-header-left" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button className="mobile-menu-toggle" onClick={() => setIsMobileOpen(!isMobileOpen)} title="Toggle Navigation Menu">
                {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <div className="breadcrumb-nav">
                <span className="breadcrumb-active">
                  {getBreadcrumbTitle()}
                </span>
              </div>
            </div>
            <div className="app-header-right">
              <div className="header-meta-group">
                <div className="user-profile-widget">
                  <div className="user-profile-main">
                    <div className="user-avatar-small" style={{ background: 'var(--danger-glow)' }}>
                      <Shield size={14} strokeWidth={2.5} color="var(--danger)" />
                    </div>
                    <div className="user-details-mini">
                      <span className="user-name-bold">{user?.username}</span>
                      <span className="user-role-mini" style={{ color: 'var(--danger)' }}>Super Admin</span>
                    </div>
                  </div>
                  <button className="icon-action-btn" onClick={onLogout} title="Logout">
                    <LogOut size={16} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          </header>

          {status && (
            <div className={`status-msg ${status.type} ${statusVisible ? 'show' : 'hide'}`}>
              {status.message}
            </div>
          )}

          <div className="view-section active">
            {activeSection !== 'serverlogs' && (loading || Object.keys(pendingDashboardRequests).length > 0) && (
              <div className="sa-card" role="status" style={{ margin: '1rem 2rem', padding: '1rem' }}>
                <Loader2 size={16} className="spinning" /> Loading dashboard data. You can continue using the dashboard.
                {Object.keys(pendingDashboardRequests).length > 0 && <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>Waiting for: {Object.values(pendingDashboardRequests).join(', ')}</div>}
              </div>
            )}
            {activeSection !== 'serverlogs' && Object.keys(dashboardErrors).length > 0 && (
              <div className="sa-card" role="alert" style={{ margin: '1rem 2rem', padding: '1rem', borderColor: 'var(--danger)' }}>
                <strong>Some dashboard data could not be loaded.</strong> Available data is still shown; previously loaded values may be out of date.
                <ul>{Object.entries(dashboardErrors).map(([key, failure]) => <li key={key}>{failure.label}: {failure.message}</li>)}</ul>
                <button className="sa-btn sa-btn-outline" onClick={loadAll} disabled={loading}><RefreshCw size={15} /> Retry dashboard data</button>
              </div>
            )}
            <div className="dashboard-layout-wrapper">

              {/* Overview Section */}
              {activeSection === 'overview' && (
                <div className="sa-section fade-in" style={{ padding: '0 2rem 2rem 2rem' }}>

                  {/* HERO SECTION */}
                  <div className="sa-overview-hero">
                    <div className="sa-hero-content">
                      <h2 className="sa-hero-title">Welcome back, SuperAdmin</h2>
                      <p className="sa-hero-subtitle">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 1rem', borderRadius: '20px', width: 'fit-content', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 10px #4ade80' }} />
                        <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{loading ? 'Refreshing system data' : Object.keys(dashboardErrors).length ? 'Some system data unavailable' : 'Dashboard data loaded'}</span>
                      </div>
                    </div>

                    <div className="sa-hero-actions">
                      <button className="sa-hero-btn" onClick={() => setActiveSection('serverlogs')}>
                        <Terminal size={18} /> Open Realtime Logs
                      </button>
                      <button className="sa-hero-btn" onClick={() => setActiveSection('users')}>
                        <Users size={18} /> Manage Users
                      </button>
                      <button className="sa-hero-btn" onClick={() => setActiveSection('infrastructure')}>
                        <Server size={18} /> Infrastructure
                      </button>
                      <button className="sa-hero-btn" onClick={loadAll}>
                        <RefreshCw size={18} /> Refresh Data
                      </button>
                    </div>
                  </div>

                  {/* FINANCIAL HEALTH */}
                  <h3 className="sa-section-title">
                    <CreditCard size={20} color="var(--primary)" /> Financial Health
                  </h3>
                  <div className="sa-widget-grid">
                    <div
                      className="sa-stat-card"
                      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1rem' }}
                      onClick={() => setActiveSection('active_subscribers_detail')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <div className="sa-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                          <Users size={24} />
                        </div>
                        <div className="sa-sparkline-container">
                          {/* Mini Sparkline for Subscribers (if chartData available, use revenue for now as proxy or just simple line) */}
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={financialStats?.chartData || []}>
                              <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div className="sa-stat-info">
                        <span className="sa-stat-value">{financialStats?.activeSubscribers || 0}</span>
                        <span className="sa-stat-label">Subscribers</span>
                      </div>
                    </div>

                    <div
                      className="sa-stat-card"
                      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1rem' }}
                      onClick={() => setActiveSection('mrr_details')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <div className="sa-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                          <CreditCard size={24} />
                        </div>
                        <div className="sa-sparkline-container">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={financialStats?.chartData || []}>
                              <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div className="sa-stat-info">
                        <span className="sa-stat-value">${Number(financialStats?.totalRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className="sa-stat-label">Total Revenue</span>
                      </div>
                    </div>

                    <div
                      className="sa-stat-card"
                      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1rem' }}
                      onClick={() => setActiveSection('churn_details')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <div className="sa-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                          <AlertCircle size={24} />
                        </div>
                        <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                          Needs Attention
                        </div>
                      </div>
                      <div className="sa-stat-info">
                        <span className="sa-stat-value">{financialStats?.churnRate || 0}%</span>
                        <span className="sa-stat-label">Est. Churn Rate</span>
                      </div>
                    </div>
                  </div>

                  {/* PLATFORM & USERS */}
                  <h3 className="sa-section-title" style={{ marginTop: '2.5rem' }}>
                    <Server size={20} color="var(--primary)" /> Platform & Users
                  </h3>
                  <div className="sa-widget-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                    <div className="sa-stat-card" style={{ cursor: 'pointer' }} onClick={() => setActiveSection('users')}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                        <Users size={24} />
                      </div>
                      <div className="sa-stat-info">
                        <span className="sa-stat-value">{totalUsers}</span>
                        <span className="sa-stat-label">Total Users</span>
                      </div>
                    </div>

                    <div className="sa-stat-card" style={{ cursor: 'pointer' }} onClick={() => setActiveSection('users')}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                        <Shield size={24} />
                      </div>
                      <div className="sa-stat-info">
                        <span className="sa-stat-value">{adminCount}</span>
                        <span className="sa-stat-label">System Admins</span>
                      </div>
                    </div>

                    <div className="sa-stat-card" style={{ cursor: 'pointer' }} onClick={() => setActiveSection('users')}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                        <UserRound size={24} />
                      </div>
                      <div className="sa-stat-info">
                        <span className="sa-stat-value">{devCount}</span>
                        <span className="sa-stat-label">Developers</span>
                      </div>
                    </div>

                    <div className="sa-stat-card" style={{ cursor: 'pointer' }} onClick={() => setActiveSection('applications')}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                        <AppWindow size={24} />
                      </div>
                      <div className="sa-stat-info">
                        <span className="sa-stat-value">{totalDeployments}</span>
                        <span className="sa-stat-label">Active Deployments</span>
                      </div>
                    </div>
                  </div>

                  {/* RECENT ACTIVITY */}
                  <h3 className="sa-section-title" style={{ marginTop: '2.5rem' }}>
                    <Activity size={20} color="var(--primary)" /> Recent Audit Activity
                  </h3>
                  <div className="sa-recent-activity-list">
                    {(auditLogs || []).slice(0, 4).map((log, i) => (
                      <div className="sa-activity-item" key={i} onClick={() => setActiveSection('audit')} style={{ cursor: 'pointer' }}>
                        <div className="sa-activity-icon" style={{ background: log.severity === 'high' ? 'rgba(239, 68, 68, 0.1)' : log.severity === 'medium' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)', color: log.severity === 'high' ? '#ef4444' : log.severity === 'medium' ? '#f59e0b' : '#3b82f6' }}>
                          {log.severity === 'high' ? <ShieldAlert size={18} /> : <Activity size={18} />}
                        </div>
                        <div className="sa-activity-content">
                          <div className="sa-activity-title">{log.action || 'System Event'}</div>
                          <div className="sa-activity-meta">
                            <span><User size={12} style={{ marginRight: '0.25rem' }} />{log.username || 'System'}</span>
                            <span>•</span>
                            <span>{new Date(log.timestamp).toLocaleString()}</span>
                            {log.ipAddress && (
                              <>
                                <span>•</span>
                                <span style={{ fontFamily: 'monospace' }}>{log.ipAddress}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <ChevronRight size={16} color="var(--text-muted)" style={{ alignSelf: 'center' }} />
                      </div>
                    ))}
                    {(!auditLogs || auditLogs.length === 0) && (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        No recent activity found.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {activeSection === 'infrastructure' && (
                <InfrastructureSection
                  systemStats={systemStats}
                  systemHealth={systemHealth}
                  superAdminApi={superAdminApi}
                  showStatus={showStatus}
                  isFullPruning={isFullPruning}
                  setIsFullPruning={setIsFullPruning}
                  isPruningImages={isPruningImages}
                  setIsPruningImages={setIsPruningImages}
                  isKillingZombies={isKillingZombies}
                  setIsKillingZombies={setIsKillingZombies}
                  loadSystemData={loadSystemData}
                  userResourceUsage={userResourceUsage}
                />
              )}
              {activeSection === 'revenue' && (
                <RevenueBillingSection
                  financialStats={financialStats}
                  revenueTimeRange={revenueTimeRange}
                  setRevenueTimeRange={setRevenueTimeRange}
                  setActiveSection={setActiveSection}
                />
              )}
              {/* Applications Section */}
              {activeSection === 'applications' && (
                <div className="sa-section fade-in">
                  {(() => {
                    const resourceMap = {};
                    if (userResourceUsage) {
                      userResourceUsage.forEach(user => {
                        user.deployments?.forEach(dep => {
                          resourceMap[dep.appName] = dep;
                        });
                      });
                    }

                    const appsWithResources = allDeployments.map(d => {
                      const isStatic = d.type === 'static' || d.type === 'zip' || d.type === 'html' || d.type === 'nginx' || d.category === 'static';
                      const liveInfo = resourceMap[d.appName];
                      return {
                        ...d,
                        resourceInfo: liveInfo
                          ? { ...liveInfo, status: isStatic ? 'running' : liveInfo.status }
                          : { status: isStatic ? 'running' : 'stopped', cpuPercent: 0, memUsageBytes: 0 }
                      };
                    });

                    const totalCount = appsWithResources.length;
                    const workingCount = appsWithResources.filter(d => d.resourceInfo.status === 'running').length;
                    const failedCount = totalCount - workingCount;

                    let filteredApps = appsWithResources.filter(d =>
                      d.appName.toLowerCase().includes(appSearch.toLowerCase()) ||
                      (d.owner && d.owner.toLowerCase().includes(appSearch.toLowerCase()))
                    );

                    if (deploymentsTab === 'working') {
                      filteredApps = filteredApps.filter(d => d.resourceInfo.status === 'running');
                    } else if (deploymentsTab === 'failed') {
                      filteredApps = filteredApps.filter(d => d.resourceInfo.status !== 'running');
                    }

                    const totalPages = Math.ceil(filteredApps.length / appsPerPage);
                    const paginatedApps = filteredApps.slice((appPage - 1) * appsPerPage, appPage * appsPerPage);

                    return (
                      <>
                        <div className="dashboard-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                          <div className="dashboard-header-title-area">
                            <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--text-main)' }}>
                              <AppWindow size={22} color="var(--primary)" /> Global Deployments
                              <span style={{ fontSize: '0.78rem', background: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary)', padding: '0.2rem 0.65rem', borderRadius: '12px', fontWeight: 600 }}>
                                {allDeployments.length} Total
                              </span>
                            </h2>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem', margin: 0 }}>
                              Real-time oversight and management of containerized applications across all users.
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                            <div className="analytics-export-dropdown-wrapper" style={{ position: 'relative' }}>
                              <button className="analytics-export-btn" onClick={() => setShowDeploymentsExportMenu(!showDeploymentsExportMenu)}>
                                <Download size={14} /> Export Report <ChevronDown size={14} />
                              </button>
                              {showDeploymentsExportMenu && (
                                <div className="analytics-export-menu" style={{ right: 0, top: '100%', marginTop: '0.4rem', zIndex: 100 }}>
                                  <button className="analytics-export-menu-item" onClick={() => { handleExportDeploymentsCSV(filteredApps, deploymentsTab); setShowDeploymentsExportMenu(false); }}>
                                    <FileText size={14} /> Export as CSV
                                  </button>
                                  <button className="analytics-export-menu-item" onClick={() => { handleExportDeploymentsJSON(filteredApps, deploymentsTab); setShowDeploymentsExportMenu(false); }}>
                                    <Code size={14} /> Export as JSON
                                  </button>
                                </div>
                              )}
                            </div>
                            <button className="sa-action-btn" style={{ padding: '0.5rem 0.9rem', height: 'auto', background: 'var(--surface)', color: 'var(--text-main)', borderColor: 'var(--border)' }} onClick={loadSystemData}>
                              <RefreshCw size={14} /> Refresh Data
                            </button>
                          </div>
                        </div>
                        {/* Segmented Filter Control & Search */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', margin: '0 1.5rem 1.5rem', flexWrap: 'wrap' }}>
                          <div style={{ display: 'inline-flex', background: 'var(--surface-hover, rgba(0, 0, 0, 0.04))', padding: '0.3rem', borderRadius: '12px', border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))', gap: '0.25rem' }}>
                            <button
                              style={{
                                border: 'none',
                                padding: '0.45rem 1rem',
                                borderRadius: '9px',
                                background: deploymentsTab === 'all' ? 'var(--surface, #fff)' : 'transparent',
                                color: deploymentsTab === 'all' ? 'var(--text-main)' : 'var(--text-muted)',
                                boxShadow: deploymentsTab === 'all' ? '0 2px 8px rgba(0, 0, 0, 0.06)' : 'none',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.45rem',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => { setDeploymentsTab('all'); setAppPage(1); }}
                            >
                              Total Deployments
                              <span style={{ fontSize: '0.75rem', padding: '0.1rem 0.5rem', borderRadius: '10px', background: deploymentsTab === 'all' ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.03)', color: 'var(--text-muted)' }}>
                                {totalCount}
                              </span>
                            </button>

                            <button
                              style={{
                                border: 'none',
                                padding: '0.45rem 1rem',
                                borderRadius: '9px',
                                background: deploymentsTab === 'working' ? 'var(--surface, #fff)' : 'transparent',
                                color: deploymentsTab === 'working' ? '#059669' : 'var(--text-muted)',
                                boxShadow: deploymentsTab === 'working' ? '0 2px 8px rgba(0, 0, 0, 0.06)' : 'none',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.45rem',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => { setDeploymentsTab('working'); setAppPage(1); }}
                            >
                              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 6px rgba(16, 185, 129, 0.5)' }}></span>
                              Working Deployments
                              <span style={{ fontSize: '0.75rem', padding: '0.1rem 0.5rem', borderRadius: '10px', background: 'rgba(16, 185, 129, 0.1)', color: '#059669', fontWeight: 700 }}>
                                {workingCount}
                              </span>
                            </button>

                            <button
                              style={{
                                border: 'none',
                                padding: '0.45rem 1rem',
                                borderRadius: '9px',
                                background: deploymentsTab === 'failed' ? 'var(--surface, #fff)' : 'transparent',
                                color: deploymentsTab === 'failed' ? '#dc2626' : 'var(--text-muted)',
                                boxShadow: deploymentsTab === 'failed' ? '0 2px 8px rgba(0, 0, 0, 0.06)' : 'none',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.45rem',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => { setDeploymentsTab('failed'); setAppPage(1); }}
                            >
                              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 6px rgba(239, 68, 68, 0.5)' }}></span>
                              Failed Deployments
                              <span style={{ fontSize: '0.75rem', padding: '0.1rem 0.5rem', borderRadius: '10px', background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', fontWeight: 700 }}>
                                {failedCount}
                              </span>
                            </button>
                          </div>

                          {/* Search box */}
                          <div className="dashboard-search-wrapper" style={{ margin: 0, width: '280px', height: '38px', borderRadius: '10px' }}>
                            <div className="dashboard-search-icon-box"><Search size={16} /></div>
                            <input type="text" className="dashboard-search-input" placeholder="Search app or owner..." value={appSearch} onChange={e => { setAppSearch(e.target.value); setAppPage(1); }} style={{ fontSize: '0.85rem' }} />
                            {appSearch && (
                              <button onClick={() => setAppSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0.5rem', color: 'var(--text-muted)' }}>
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        </div>

                        {filteredApps.length === 0 ? (
                          <div className="empty-users-state" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                            <LayoutTemplate size={48} color="var(--text-tertiary)" style={{ marginBottom: '1rem' }} />
                            <h3 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.1rem' }}>No Deployments Found</h3>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>No application matches your current tab or search query.</p>
                          </div>
                        ) : (
                          <div className="sa-table-wrapper" style={{ margin: '0 1.5rem' }}>
                            <table className="sa-table">
                              <thead>
                                {deploymentsTab === 'failed' ? (
                                  <tr>
                                    <th>App Name</th>
                                    <th>Owner</th>
                                    <th>Deployment Type</th>
                                    <th>Status</th>
                                    <th>Error / Failure Reason</th>
                                    <th style={{ textAlign: 'right', paddingRight: '1rem' }}>Actions</th>
                                  </tr>
                                ) : (
                                  <tr>
                                    <th>App Name</th>
                                    <th>Owner</th>
                                    <th>Status</th>
                                    <th>Type</th>
                                    <th>Port</th>
                                    <th>CPU</th>
                                    <th>RAM</th>
                                    <th style={{ textAlign: 'right', paddingRight: '1rem' }}>Actions</th>
                                  </tr>
                                )}
                              </thead>
                              <tbody>
                                {paginatedApps.map((d, i) => {
                                  const errorMsg = d.metadata?.error || d.metadata?.lastError || d.resourceInfo?.error || d.resourceInfo?.message || 'Container exited unexpectedly (process stopped)';

                                  if (deploymentsTab === 'failed') {
                                    return (
                                      <tr key={i}>
                                        <td>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                                            <Server size={15} color="#ef4444" style={{ opacity: 0.85 }} />
                                            <span className="sa-cell-bold">{d.appName}</span>
                                          </div>
                                        </td>
                                        <td>
                                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'var(--surface-hover, rgba(0,0,0,0.04))', color: 'var(--text-main)', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))' }}>
                                            <User size={12} style={{ opacity: 0.6 }} />
                                            {d.owner}
                                          </div>
                                        </td>
                                        <td>
                                          <span style={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', padding: '0.15rem 0.45rem', borderRadius: '4px', background: 'var(--surface-hover, rgba(0,0,0,0.04))', color: 'var(--text-muted)', border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))' }}>
                                            {d.type || 'docker'}
                                          </span>
                                        </td>
                                        <td>
                                          <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', padding: '0.25rem 0.65rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 6px rgba(239, 68, 68, 0.6)' }}></span>
                                            Failed / Stopped
                                          </span>
                                        </td>
                                        <td>
                                          <div style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '6px', padding: '0.3rem 0.6rem', color: '#b91c1c', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.78rem', maxWidth: '340px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.35rem' }} title={errorMsg}>
                                            <AlertCircle size={13} style={{ flexShrink: 0 }} />
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{errorMsg}</span>
                                          </div>
                                        </td>
                                        <td className="sa-actions-cell" style={{ paddingRight: '0.5rem' }}>
                                          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                                            <button className="sa-action-btn sa-action-env" onClick={() => handleInspectEnv(d.appName)} title="Inspect Environment Variables">
                                              <Sliders size={14} /> Env
                                            </button>
                                            <button className="sa-action-btn sa-action-restart" onClick={() => handleAppAction(d.appName, 'restart')} title="Restart Container">
                                              <RefreshCw size={14} /> Restart
                                            </button>
                                            <button className="sa-action-btn sa-action-delete" onClick={() => handleAppAction(d.appName, 'delete')} title="Force Delete Container">
                                              <Trash2 size={14} /> Delete
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  }

                                  return (
                                    <tr key={i}>
                                      <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                                          <Server size={15} color="var(--primary)" style={{ opacity: 0.85 }} />
                                          <span className="sa-cell-bold">{d.appName}</span>
                                        </div>
                                      </td>
                                      <td>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'var(--surface-hover, rgba(0,0,0,0.04))', color: 'var(--text-main)', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))' }}>
                                          <User size={12} style={{ opacity: 0.6 }} />
                                          {d.owner}
                                        </div>
                                      </td>
                                      <td>
                                        {d.resourceInfo.status === 'running' ? (
                                          <span style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#16a34a', padding: '0.25rem 0.65rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px rgba(34, 197, 94, 0.6)' }}></span>
                                            Running
                                          </span>
                                        ) : (
                                          <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', padding: '0.25rem 0.65rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 6px rgba(239, 68, 68, 0.6)' }}></span>
                                            Stopped/Failed
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        <span style={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', padding: '0.15rem 0.45rem', borderRadius: '4px', background: 'var(--surface-hover, rgba(0,0,0,0.04))', color: 'var(--text-muted)', border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))' }}>
                                          {d.type || 'docker'}
                                        </span>
                                      </td>
                                      <td>
                                        <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600, color: 'var(--primary)', background: 'rgba(59, 130, 246, 0.08)', padding: '0.15rem 0.5rem', borderRadius: '6px', fontSize: '0.8rem' }}>
                                          {d.port ? `:${d.port}` : '—'}
                                        </span>
                                      </td>
                                      <td>
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 500 }}>
                                          {d.resourceInfo.cpuPercent?.toFixed(2) || '0.00'}%
                                        </span>
                                      </td>
                                      <td>
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 500 }}>
                                          {formatBytesValue(d.resourceInfo.memUsageBytes || 0)}
                                        </span>
                                      </td>
                                      <td className="sa-actions-cell" style={{ paddingRight: '0.5rem' }}>
                                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                                          <button className="sa-action-btn sa-action-env" onClick={() => handleInspectEnv(d.appName)} title="Inspect Environment Variables">
                                            <Sliders size={14} /> Env
                                          </button>
                                          <button className="sa-action-btn sa-action-restart" onClick={() => handleAppAction(d.appName, 'restart')} title="Restart Container">
                                            <RefreshCw size={14} /> Restart
                                          </button>
                                          <button className="sa-action-btn sa-action-delete" onClick={() => handleAppAction(d.appName, 'delete')} title="Force Delete Container">
                                            <Trash2 size={14} /> Delete
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>

                            {totalPages > 1 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem', padding: '0.5rem 0.5rem 0' }}>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                                  Showing <strong style={{ color: 'var(--text-main)' }}>{((appPage - 1) * appsPerPage) + 1}</strong> to <strong style={{ color: 'var(--text-main)' }}>{Math.min(appPage * appsPerPage, filteredApps.length)}</strong> of <strong style={{ color: 'var(--text-main)' }}>{filteredApps.length}</strong> deployments
                                </div>
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                  <button className="sa-action-btn" disabled={appPage === 1} onClick={() => setAppPage(p => Math.max(1, p - 1))} style={{ padding: '0.4rem 0.75rem' }}>
                                    <ChevronLeft size={14} /> Previous
                                  </button>
                                  <button className="sa-action-btn" disabled={appPage === totalPages} onClick={() => setAppPage(p => Math.min(totalPages, p + 1))} style={{ padding: '0.4rem 0.75rem' }}>
                                    Next <ChevronRight size={14} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Env Inspector Modal */}
                  {viewingEnvApp && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div className="sa-card" style={{ width: '600px', maxWidth: '90%', margin: 0, position: 'relative' }}>
                        <div className="sa-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h3>Environment Variables: {viewingEnvApp}</h3>
                          <button className="active-deployment-dismiss" onClick={() => setViewingEnvApp(null)}><X size={20} /></button>
                        </div>
                        <div style={{ padding: '1.5rem' }}>
                          {isEnvLoading ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}><RefreshCw size={24} className="spin" /></div>
                          ) : (
                            <pre style={{ background: '#1e1e1e', padding: '1rem', borderRadius: '8px', overflowX: 'auto', color: '#e5e5e5', fontSize: '0.9rem', border: '1px solid #333' }}>
                              {envData || 'No environment variables found.'}
                            </pre>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {activeSection === 'packages' && (
                <PricingPackagesSection
                  packages={packages}
                  setPackages={setPackages}
                  api={api}
                  showStatus={showStatus}
                />
              )}

              {/* Settings Section */}
              {activeSection === 'settings' && (
                <div className="sa-section fade-in">
                  <div className="dashboard-header">
                    <div className="dashboard-header-title-area">
                      <h2>System Configuration</h2>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>Global settings, quotas, and environment variables.</p>
                    </div>
                    <button className="action-btn-premium primary" disabled={isSavingSettings} onClick={async () => {
                      setIsSavingSettings(true);
                      try {
                        await superAdminApi.updateSystemSettings(lifecycleSettings);
                        showStatus('Settings saved globally', 'success');
                      } catch (e) {
                        showStatus('Failed to save settings', 'error');
                      } finally {
                        setIsSavingSettings(false);
                      }
                    }}>
                      {isSavingSettings ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                  <div className="sa-form-card" style={{ padding: '1.5rem', margin: '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--text-main)', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem' }}>Subscription Lifecycle</h3>
                    <div className="form-group" style={{ maxWidth: '400px' }}>
                      <label style={{ color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: '0.5rem', display: 'block' }}>Payment Grace Period (Days)</label>
                      <input type="number" className="premium-input" value={lifecycleSettings.gracePeriodDays} onChange={e => setLifecycleSettings({ ...lifecycleSettings, gracePeriodDays: e.target.value })} />
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Number of days to keep deployments running after payment fails before downgrading.</p>
                    </div>
                    <div className="form-group" style={{ maxWidth: '400px' }}>
                      <label style={{ color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: '0.5rem', display: 'block' }}>Data Retention Period (Days)</label>
                      <input type="number" className="premium-input" value={lifecycleSettings.retentionPeriodDays} onChange={e => setLifecycleSettings({ ...lifecycleSettings, retentionPeriodDays: e.target.value })} />
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Number of days to retain downgraded deployments before purging.</p>
                    </div>
                    <div className="form-group" style={{ maxWidth: '400px' }}>
                      <label style={{ color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: '0.5rem', display: 'block' }}>Notification Frequency (Days)</label>
                      <input type="number" className="premium-input" value={lifecycleSettings.notificationFrequencyDays} onChange={e => setLifecycleSettings({ ...lifecycleSettings, notificationFrequencyDays: e.target.value })} />
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>How often to send a reminder email during the grace period.</p>
                    </div>

                    <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--text-main)', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem', marginTop: '1rem' }}>Global Access Control</h3>
                    <div className="form-group" style={{ maxWidth: '400px', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <input type="checkbox" id="maintenanceMode" checked={lifecycleSettings.maintenanceMode || false} onChange={e => setLifecycleSettings({ ...lifecycleSettings, maintenanceMode: e.target.checked })} style={{ width: '1.2rem', height: '1.2rem', accentColor: 'var(--primary)' }} />
                      <div>
                        <label htmlFor="maintenanceMode" style={{ color: 'var(--text-light)', fontSize: '0.85rem', margin: 0, display: 'block', cursor: 'pointer' }}>Enable Maintenance Mode</label>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>Blocks all non-admin access to the dashboard.</p>
                      </div>
                    </div>
                      <div className="form-group" style={{ maxWidth: '400px', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <input type="checkbox" id="disableSignups" checked={lifecycleSettings.disableSignups || false} onChange={e => setLifecycleSettings({ ...lifecycleSettings, disableSignups: e.target.checked })} style={{ width: '1.2rem', height: '1.2rem', accentColor: 'var(--primary)' }} />
                        <div>
                          <label htmlFor="disableSignups" style={{ color: 'var(--text-light)', fontSize: '0.85rem', margin: 0, display: 'block', cursor: 'pointer' }}>Disable New Signups</label>
                          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>Prevents new users from creating accounts.</p>
                        </div>
                      </div>
                      
                      <div className="form-group" style={{ maxWidth: '400px', display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
                        <input type="checkbox" id="enableGitBackendWorkspace" checked={lifecycleSettings.enableGitBackendWorkspace !== false} onChange={e => setLifecycleSettings({ ...lifecycleSettings, enableGitBackendWorkspace: e.target.checked })} style={{ width: '1.2rem', height: '1.2rem', accentColor: 'var(--primary)' }} />
                        <div>
                          <label htmlFor="enableGitBackendWorkspace" style={{ color: 'var(--text-light)', fontSize: '0.85rem', margin: 0, display: 'block', cursor: 'pointer' }}>Enable Git Backend Workspaces</label>
                          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>If disabled, users can only deploy static sites via Git.</p>
                        </div>
                      </div>
                      
                      <div className="form-group" style={{ maxWidth: '400px', display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
                        <input type="checkbox" id="enableGitAutoExecution" checked={lifecycleSettings.enableGitAutoExecution !== false} onChange={e => setLifecycleSettings({ ...lifecycleSettings, enableGitAutoExecution: e.target.checked })} style={{ width: '1.2rem', height: '1.2rem', accentColor: 'var(--primary)' }} />
                        <div>
                          <label htmlFor="enableGitAutoExecution" style={{ color: 'var(--text-light)', fontSize: '0.85rem', margin: 0, display: 'block', cursor: 'pointer' }}>Enable Automated AI Execution in Git Workspaces</label>
                          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>If disabled, users must manually start their apps via terminal after Git import.</p>
                        </div>
                      </div>

                      <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--text-main)', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem', marginTop: '2rem' }}>AI Endpoints (Fallback)</h3>
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Configure multiple OpenAI-compatible endpoints. The system will try them in order and automatically fallback to the next one if an API fails.</p>

                      <div className="form-group" style={{ maxWidth: '400px', marginBottom: '1.5rem', background: 'var(--bg-card)', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                        <label style={{ color: 'var(--text-main)', fontSize: '0.9rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Active AI Group</label>
                        <select className="vercel-input" value={lifecycleSettings.activeAiGroup || ''} onChange={e => setLifecycleSettings({ ...lifecycleSettings, activeAiGroup: e.target.value })} style={{ width: '100%', fontSize: '0.85rem', padding: '0.5rem' }}>
                          <option value="">All Endpoints (No Group Filter)</option>
                          {[...new Set((lifecycleSettings.aiEndpoints || []).map(ep => ep.groupName).filter(Boolean))].map(group => (
                            <option key={group} value={group}>{group}</option>
                          ))}
                        </select>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem', marginBottom: 0 }}>Select which group of endpoints to actively use for AI tasks. If blank, it will use all endpoints below.</p>
                      </div>

                      {(() => {
                        const getDomain = (url) => {
                          try {
                            if (!url) return 'Unknown';
                            const hostname = new URL(url).hostname;
                            return hostname.replace(/^api\.|^www\./, '');
                          } catch {
                            return 'Unknown';
                          }
                        };
                        const uniqueProviders = ['All', ...new Set((lifecycleSettings.aiEndpoints || []).map(ep => getDomain(ep.url)))].filter(p => p !== 'Unknown' || p === 'All');
                        const filteredEndpoints = (lifecycleSettings.aiEndpoints || []).map((ep, idx) => ({ ...ep, originalIdx: idx })).filter(ep => {
                          if (aiEndpointFilter === 'All') return true;
                          return getDomain(ep.url) === aiEndpointFilter;
                        });

                        return (
                          <>
                            {uniqueProviders.length > 2 && (
                              <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <label style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>Filter by Provider:</label>
                                <select 
                                  className="vercel-input" 
                                  value={aiEndpointFilter} 
                                  onChange={e => setAiEndpointFilter(e.target.value)}
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', maxWidth: '250px' }}
                                >
                                  {uniqueProviders.map(provider => (
                                    <option key={provider} value={provider}>{provider === 'All' ? 'All Providers' : provider}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {(() => {
                              const groupedEndpoints = {};
                              filteredEndpoints.forEach(ep => {
                                const g = ep.groupName || 'Unassigned';
                                if (!groupedEndpoints[g]) groupedEndpoints[g] = [];
                                groupedEndpoints[g].push(ep);
                              });
                              
                              return Object.entries(groupedEndpoints).map(([groupName, eps]) => {
                                const isExpanded = expandedAiGroups[groupName] !== false; // Default expanded
                                return (
                                <div key={groupName} className="endpoint-group" style={{ marginBottom: '2rem', maxWidth: '600px', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                                  <div style={{ background: 'var(--surface)', padding: '1rem', borderBottom: isExpanded ? '1px solid var(--border-color)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                                    <div 
                                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', flex: 1 }}
                                      onClick={() => setExpandedAiGroups(prev => ({ ...prev, [groupName]: !isExpanded }))}
                                    >
                                      {isExpanded ? <ChevronDown size={18} color="var(--text-light)" /> : <ChevronRight size={18} color="var(--text-light)" />}
                                      <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <Folder size={16} /> {groupName}
                                        <span style={{ fontSize: '0.75rem', background: 'var(--border-color)', padding: '0.1rem 0.4rem', borderRadius: '12px' }}>{eps.length}</span>
                                      </h3>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                      <button className="vercel-button-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={() => {
                                        const bulkKey = window.prompt(`Enter new API Key for all endpoints in "${groupName}":`);
                                        if (bulkKey !== null) {
                                          const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                          eps.forEach(ep => {
                                            newEndpoints[ep.originalIdx].key = bulkKey.trim();
                                          });
                                          setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                        }
                                      }}>
                                        <Key size={12} /> Set Group Key
                                      </button>
                                      <button className="vercel-button-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={() => {
                                        const bulkUrl = window.prompt(`Enter new Base URL for all endpoints in "${groupName}":`);
                                        if (bulkUrl !== null) {
                                          const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                          eps.forEach(ep => {
                                            newEndpoints[ep.originalIdx].url = bulkUrl.trim();
                                          });
                                          setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                        }
                                      }}>
                                        <Link size={12} /> Set Group URL
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {isExpanded && (
                                  <div style={{ padding: '1rem 1rem 0 1rem' }}>
                                    {eps.map((ep) => {
                                      const idx = ep.originalIdx;
                                      return (
                                        <div key={idx} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                              <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>Endpoint {idx + 1}</strong>
                                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-light)', cursor: 'pointer' }}>
                                                <input 
                                                  type="checkbox" 
                                                  name={`primaryAiEndpoint_${idx}`}
                                                  checked={ep.isPrimary || false}
                                                  onChange={(e) => {
                                                    const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                                    newEndpoints[idx] = { ...newEndpoints[idx], isPrimary: e.target.checked };
                                                    setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                                  }}
                                                  style={{ accentColor: 'var(--primary)' }}
                                                />
                                                Set as Priority
                                              </label>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <button 
                                                className="vercel-button-secondary" 
                                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }} 
                                                disabled={testingAiEndpointIdx === idx}
                                                onClick={() => handleTestAiEndpoint(idx, ep)}
                                              >
                                                {testingAiEndpointIdx === idx ? <Loader2 size={12} className="spinning" /> : <Play size={12} />}
                                                Test Connection
                                              </button>
                                              <button className="vercel-button-danger" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => {
                                                const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                                newEndpoints.splice(idx, 1);
                                                if (ep.isPrimary && newEndpoints.length > 0) {
                                                  newEndpoints[0].isPrimary = true;
                                                }
                                                setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                              }}>Remove</button>
                                            </div>
                                          </div>
                                          <div className="form-group" style={{ marginBottom: '1rem' }}>
                                            <label style={{ color: 'var(--text-light)', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>OpenAI-Compatible Base URL</label>
                                            <input type="text" className="vercel-input" value={ep.url || ''} onChange={e => {
                                              const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                              newEndpoints[idx].url = e.target.value;
                                              setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                            }} placeholder="https://api.openai.com/v1/chat/completions" style={{ width: '100%', fontSize: '0.85rem' }} />
                                          </div>
                                          <div className="form-group" style={{ marginBottom: '1rem' }}>
                                            <label style={{ color: 'var(--text-light)', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>API Key</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <input
                                                type={visibleAiKeys[idx] ? 'text' : 'password'}
                                                className="vercel-input"
                                                value={ep.key || ''}
                                                onChange={e => {
                                                  const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                                  newEndpoints[idx] = { ...newEndpoints[idx], key: e.target.value };
                                                  setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                                }}
                                                placeholder="sk-..."
                                                autoComplete="off"
                                                spellCheck={false}
                                                style={{ flex: 1, width: '100%', fontSize: '0.85rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                                              />
                                              <button
                                                type="button"
                                                title={visibleAiKeys[idx] ? 'Hide API key' : 'Show API key'}
                                                onClick={() => setVisibleAiKeys(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                                style={{
                                                  flexShrink: 0,
                                                  height: '36px',
                                                  width: '36px',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  background: 'var(--surface)',
                                                  color: 'var(--text-main)',
                                                  border: '1px solid var(--border-color, var(--border))',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                {visibleAiKeys[idx] ? <EyeOff size={16} /> : <Eye size={16} />}
                                              </button>
                                              <button
                                                type="button"
                                                title="Copy API key"
                                                disabled={!ep.key}
                                                onClick={async () => {
                                                  const key = String(ep.key || '');
                                                  if (!key) return;
                                                  try {
                                                    await navigator.clipboard.writeText(key);
                                                  } catch (_) {
                                                    const ta = document.createElement('textarea');
                                                    ta.value = key;
                                                    document.body.appendChild(ta);
                                                    ta.select();
                                                    document.execCommand('copy');
                                                    document.body.removeChild(ta);
                                                  }
                                                  setCopiedAiKeyIdx(idx);
                                                  setTimeout(() => setCopiedAiKeyIdx(prev => (prev === idx ? null : prev)), 2000);
                                                }}
                                                style={{
                                                  flexShrink: 0,
                                                  height: '36px',
                                                  minWidth: '36px',
                                                  padding: '0 0.55rem',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '0.3rem',
                                                  background: 'var(--surface)',
                                                  color: copiedAiKeyIdx === idx ? 'var(--success)' : 'var(--text-main)',
                                                  border: '1px solid var(--border-color, var(--border))',
                                                  borderRadius: '6px',
                                                  cursor: ep.key ? 'pointer' : 'not-allowed',
                                                  opacity: ep.key ? 1 : 0.5,
                                                  fontSize: '0.75rem',
                                                  fontWeight: 700
                                                }}
                                              >
                                                {copiedAiKeyIdx === idx ? <Check size={16} /> : <Copy size={16} />}
                                              </button>
                                            </div>
                                          </div>
                                          <div className="form-group" style={{ marginBottom: '1rem' }}>
                                            <label style={{ color: 'var(--text-light)', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Group Name (Optional)</label>
                                            <input type="text" className="vercel-input" value={ep.groupName || ''} onChange={e => {
                                              const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                              newEndpoints[idx].groupName = e.target.value;
                                              setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                            }} placeholder="e.g. GPT-4 APIs" style={{ width: '100%', fontSize: '0.85rem' }} />
                                          </div>
                                          <div className="form-group" style={{ marginBottom: '0' }}>
                                            <label style={{ color: 'var(--text-light)', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Model Name</label>
                                            <input type="text" className="vercel-input" value={ep.model || ''} onChange={e => {
                                              const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                              newEndpoints[idx].model = e.target.value;
                                              setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                            }} placeholder="gpt-4o-mini" style={{ width: '100%', fontSize: '0.85rem' }} />
                                          </div>
                                          <div className="form-group" style={{ marginTop: '1rem', marginBottom: '0' }}>
                                            <label style={{ color: 'var(--text-light)', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Role</label>
                                            <select className="vercel-input" value={ep.role || 'general'} onChange={e => {
                                              const newEndpoints = [...(lifecycleSettings.aiEndpoints || [])];
                                              newEndpoints[idx] = { ...newEndpoints[idx], role: e.target.value };
                                              setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                                            }} style={{ width: '100%', fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                                              <option value="general">General (Fallback)</option>
                                              <option value="master">Master (Planner — uses smarter model)</option>
                                              <option value="worker">Worker (Executor — can use cheaper/faster model)</option>
                                            </select>
                                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.4rem 0 0 0' }}>
                                              {ep.role === 'master'
                                                ? 'Analyzes projects and creates task plans for workers. Recommended: GPT-4o, Claude Sonnet, or similar capable model.'
                                                : ep.role === 'worker'
                                                ? 'Executes tasks assigned by the master agent in parallel. Can use faster/cheaper models for speed.'
                                                : 'Used as a standard endpoint with automatic fallback. Works for both single-agent and master-worker flows.'}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  )}
                                </div>
                                );
                              });
                            })()}
                          </>
                        );
                      })()}
                      <div style={{ maxWidth: '600px', marginBottom: '1.25rem' }}>
                        <button className="vercel-button" style={{ width: '100%', background: 'transparent', border: '1px dashed var(--border-color)', color: 'var(--text-main)' }} onClick={() => {
                          const newEndpoints = [...(lifecycleSettings.aiEndpoints || []), { url: '', key: '', model: '', role: 'general' }];
                          setLifecycleSettings({ ...lifecycleSettings, aiEndpoints: newEndpoints });
                        }}>+ Add AI Endpoint</button>
                      </div>

                  </div>
                </div>
              )}

              {/* User Management Section */}
              {activeSection === 'users' && (
                <SystemUsersSection
                  showUserForm={showUserForm} setShowUserForm={setShowUserForm} clearUserForm={clearUserForm}
                  handleUserSubmit={handleUserSubmit} editingUser={editingUser} startEditUser={startEditUser} handleUserDelete={handleUserDelete}
                  newUsername={newUsername} setNewUsername={setNewUsername}
                  newFullName={newFullName} setNewFullName={setNewFullName}
                  newEmail={newEmail} setNewEmail={setNewEmail}
                  newRole={newRole} setNewRole={setNewRole}
                  newSubscriptionStatus={newSubscriptionStatus} setNewSubscriptionStatus={setNewSubscriptionStatus}
                  newPassword={newPassword} setNewPassword={setNewPassword} isSubmitting={isSubmitting}
                  accountType={accountType} setAccountType={setAccountType}
                  packageMode={packageMode} setPackageMode={setPackageMode}
                  selectedPackage={selectedPackage} setSelectedPackage={setSelectedPackage}
                  customLimitsForm={customLimitsForm} setCustomLimitsForm={setCustomLimitsForm}
                  sendWelcomeEmail={sendWelcomeEmail} setSendWelcomeEmail={setSendWelcomeEmail}
                  packages={packages}
                  userFilter={userFilter} setUserFilter={setUserFilter} userRoleFilter={userRoleFilter} setUserRoleFilter={setUserRoleFilter}
                  userStatusFilter={userStatusFilter} setUserStatusFilter={setUserStatusFilter} userSort={userSort} setUserSort={setUserSort}
                  setCurrentPage={setCurrentPage} paginatedUsers={paginatedUsers} filteredUsers={filteredUsers}
                  selectedUserIds={selectedUserIds} setSelectedUserIds={setSelectedUserIds} toggleAllSelection={toggleAllSelection} toggleUserSelection={toggleUserSelection}
                  viewingUser={viewingUser} setViewingUser={setViewingUser} setEditingLimitsForUser={setEditingLimitsForUser} setCustomLimits={setCustomLimits}
                  users={users} userResourceUsage={userResourceUsage} setSelectedUserResourceDetails={setSelectedUserResourceDetails} setActiveSection={setActiveSection}
                  api={api} superAdminApi={superAdminApi} showStatus={showStatus} loadAll={loadAll} user={user} formatBytesValue={formatBytesValue}
                  setSelectedTeamMember={setSelectedTeamMember} setSelectedTeamMemberParent={setSelectedTeamMemberParent}
                  setSelectedAdminForMembers={setSelectedAdminForMembers}
                />
              )}

              {/* Admin Team Members Section */}
              {activeSection === 'admin_members_list' && selectedAdminForMembers && (
                <AdminTeamMembersSection
                  adminUser={selectedAdminForMembers}
                  users={users}
                  setActiveSection={setActiveSection}
                  setSelectedTeamMember={setSelectedTeamMember}
                  setSelectedTeamMemberParent={setSelectedTeamMemberParent}
                />
              )}

              {/* Team Member Details Section */}
              {activeSection === 'team_member_details' && selectedTeamMember && (
                <TeamMemberDetailsSection
                  member={selectedTeamMember}
                  parentAdmin={selectedTeamMemberParent}
                  userResourceUsage={userResourceUsage}
                  allDeployments={allDeployments}
                  setActiveSection={setActiveSection}
                  handleUserDelete={handleUserDelete}
                  handleToggleActive={handleToggleActive}
                  formatBytesValue={formatBytesValue}
                />
              )}
              {activeSection === 'announcements' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <div>
                      <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}><Activity size={24} strokeWidth={2.0} /> Global Broadcasts</h2>
                      <p className="subtitle">Push a global notification banner to all developers.</p>
                    </div>
                  </div>
                  <div className="sa-card" style={{ padding: '2.5rem' }}>
                    <div style={{ maxWidth: '650px' }}>
                      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          <Megaphone size={16} /> Announcement Message
                        </label>
                        <textarea
                          className="premium-input"
                          rows="5"
                          placeholder="e.g. Scheduled maintenance this Saturday at 2AM UTC."
                          value={announcementMsg}
                          onChange={(e) => setAnnouncementMsg(e.target.value)}
                          style={{ width: '100%', resize: 'vertical', padding: '1rem', borderRadius: '12px', background: '#f8fafc', border: '1px solid #cbd5e1', fontSize: '0.95rem', lineHeight: '1.5', transition: 'all 0.2s', outline: 'none' }}
                          onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #3b82f6)'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#cbd5e1'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'none'; }}
                        />
                        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                          This message will be prominently displayed at the top of the dashboard for all users.
                        </div>
                      </div>

                      <div className="form-group" style={{ marginBottom: '2.5rem', display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                          <input
                            type="checkbox"
                            id="announcementActive"
                            checked={announcementActive}
                            onChange={(e) => setAnnouncementActive(e.target.checked)}
                            style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                          />
                          <label
                            htmlFor="announcementActive"
                            style={{
                              position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                              backgroundColor: announcementActive ? 'var(--primary, #3b82f6)' : '#cbd5e1',
                              transition: '.4s', borderRadius: '34px'
                            }}
                          >
                            <span style={{
                              position: 'absolute', content: '""', height: '18px', width: '18px',
                              left: announcementActive ? '22px' : '3px', bottom: '3px',
                              backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                            }} />
                          </label>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text-main, #0f172a)', fontSize: '0.95rem' }}>Banner Active</div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.1rem' }}>Toggle to instantly show or hide this broadcast across the platform.</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <button
                          className="sa-btn"
                          onClick={saveAnnouncement}
                          style={{ padding: '0.85rem 2.5rem', background: 'var(--primary, #0f172a)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', transition: 'all 0.2s', fontSize: '0.9rem' }}
                          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'; }}
                          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'; }}
                        >
                          <Megaphone size={18} /> Publish Broadcast
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'security' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <div>
                      <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}><ShieldAlert size={24} strokeWidth={2.0} /> Security & WAF</h2>
                      <p className="subtitle">Manage Web Application Firewall rules to block malicious IPs globally.</p>
                    </div>
                  </div>
                  <div className="sa-card" style={{ padding: '2.5rem', marginBottom: '2rem' }}>
                    <div style={{ maxWidth: '650px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <ShieldAlert size={16} /> IP Address to Ban
                      </label>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <div className="form-group" style={{ flex: 1, margin: 0 }}>
                          <input
                            type="text"
                            placeholder="e.g. 192.168.1.100"
                            value={newBanIp}
                            onChange={(e) => setNewBanIp(e.target.value)}
                            style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '8px', background: '#f8fafc', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'all 0.2s', outline: 'none' }}
                            onFocus={(e) => { e.target.style.borderColor = '#ef4444'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#cbd5e1'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'none'; }}
                          />
                        </div>
                        <button
                          className="sa-btn"
                          onClick={addBanIp}
                          disabled={!newBanIp.trim()}
                          style={{ padding: '0.85rem 2rem', background: newBanIp.trim() ? '#ef4444' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: newBanIp.trim() ? 'pointer' : 'not-allowed', transition: 'all 0.2s', boxShadow: newBanIp.trim() ? '0 4px 6px -1px rgba(239, 68, 68, 0.3)' : 'none' }}
                        >
                          Ban IP
                        </button>
                      </div>
                      <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                        Banning an IP prevents it from accessing any part of the platform, including user apps.
                      </div>
                    </div>
                  </div>

                  <div className="sa-table-wrapper" style={{ marginBottom: '1.5rem' }}>
                    <table className="sa-table">
                      <thead>
                        <tr>
                          <th>Banned IP Address</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const bList = securityData.bannedIPs || [];
                          if (bList.length === 0) {
                            return (
                              <tr>
                                <td colSpan="2" className="sa-empty-cell">
                                  <ShieldAlert size={32} color="var(--text-tertiary)" style={{ margin: '0 auto 1rem' }} />
                                  <div>No IPs are currently banned.</div>
                                </td>
                              </tr>
                            );
                          }
                          const paginated = bList.slice((bannedIPsPage - 1) * saItemsPerPage, bannedIPsPage * saItemsPerPage);
                          return paginated.map(ip => (
                            <tr key={ip}>
                              <td style={{ fontWeight: 600, color: 'var(--text-main)' }}>{ip}</td>
                              <td className="sa-actions-cell" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                <button
                                  className="sa-action-btn sa-action-delete"
                                  onClick={() => removeBanIp(ip)}
                                  title="Unban IP"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                    {renderPaginationControls(bannedIPsPage, Math.ceil((securityData.bannedIPs || []).length / saItemsPerPage) || 1, (securityData.bannedIPs || []).length, saItemsPerPage, setBannedIPsPage, "IPs")}
                  </div>
                </div>
              )}

              {/* System Health & Errors Section */}
              {activeSection === 'health' && (
                <div className="sa-section fade-in">
                  <div className="dashboard-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div className="dashboard-header-title-area">
                      <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--text-main)' }}>
                        <Activity size={22} color="var(--primary)" /> System Health & Error Monitoring
                        <span style={{ fontSize: '0.78rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '0.2rem 0.65rem', borderRadius: '12px', fontWeight: 600 }}>
                          {(systemErrors || []).filter(e => e.status === 'investigating' || !e.status).length} Unresolved
                        </span>
                      </h2>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem', margin: 0 }}>
                        Error summary from the latest 1,000 backend events. Open Realtime Logs for incoming events and deployment progress.
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="sa-action-btn" style={{ padding: '0.5rem 0.9rem', background: 'var(--surface)', color: 'var(--text-main)', borderColor: 'var(--border)' }} onClick={async () => {
                        try {
                          const errRes = await superAdminApi.getSystemErrors();
                          setSystemErrors(errRes.errors || []);
                          showStatus('System logs refreshed from server', 'success');
                        } catch (e) {
                          showStatus('Failed to refresh system logs', 'error');
                        }
                      }}>
                        <RefreshCw size={14} /> Refresh Logs
                      </button>
                      <button className="sa-action-btn" style={{ padding: '0.5rem 0.9rem', color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)' }} onClick={async () => {
                        setSystemErrors(prev => (prev || []).map(e => ({ ...e, status: 'resolved' })));
                        showStatus('All active items marked as resolved', 'success');
                        try {
                          await Promise.all((systemErrors || []).map(e => superAdminApi.updateSystemErrorStatus({ key: `${e.code}-${e.endpoint}`, status: 'resolved' })));
                        } catch (e) { }
                      }}>
                        <CheckSquare size={14} /> Mark All Resolved
                      </button>
                    </div>
                  </div>

                  {/* Summary Metric Cards */}
                  <div className="premium-stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', margin: '0 1.5rem 1.5rem' }}>
                    <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>Total Logged Errors</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>{(systemErrors || []).length}</div>
                    </div>
                    <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>Unresolved Errors</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ef4444', marginTop: '0.2rem' }}>
                        {(systemErrors || []).filter(e => e.status === 'investigating' || !e.status).length}
                      </div>
                    </div>
                    <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>500 Critical Errors</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f59e0b', marginTop: '0.2rem' }}>
                        {(systemErrors || []).filter(e => e.code && e.code.includes('500')).length}
                      </div>
                    </div>
                    <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>Resolved Errors</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981', marginTop: '0.2rem' }}>
                        {(systemErrors || []).filter(e => e.status === 'resolved').length}
                      </div>
                    </div>
                  </div>

                  {/* Filter & Search Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', margin: '0 1.5rem 1.5rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'inline-flex', background: 'var(--surface-hover, rgba(0, 0, 0, 0.04))', padding: '0.3rem', borderRadius: '12px', border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))', gap: '0.25rem' }}>
                      {[
                        { id: 'ALL', label: 'All Errors' },
                        { id: 'UNRESOLVED', label: 'Unresolved' },
                        { id: 'CRITICAL_500', label: '500 Server Errors' },
                        { id: 'RESOLVED', label: 'Resolved' }
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => { setErrorFilterType(tab.id); setSystemErrorsPage(1); }}
                          style={{
                            padding: '0.4rem 0.85rem',
                            borderRadius: '8px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            background: errorFilterType === tab.id ? 'var(--surface)' : 'transparent',
                            color: errorFilterType === tab.id ? 'var(--primary)' : 'var(--text-muted)',
                            boxShadow: errorFilterType === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                          }}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    <div style={{ position: 'relative', width: '280px' }}>
                      <Search size={16} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        type="text"
                        placeholder="Search endpoint or error code..."
                        value={errorSearchQuery}
                        onChange={e => { setErrorSearchQuery(e.target.value); setSystemErrorsPage(1); }}
                        style={{
                          width: '100%',
                          padding: '0.45rem 0.85rem 0.45rem 2.2rem',
                          borderRadius: '10px',
                          border: '1px solid var(--border)',
                          background: 'var(--surface)',
                          color: 'var(--text-main)',
                          fontSize: '0.85rem'
                        }}
                      />
                    </div>
                  </div>

                  <div className="sa-table-wrapper" style={{ margin: '0 1.5rem 1.5rem' }}>
                    <table className="sa-table">
                      <thead>
                        <tr>
                          <th>Error Code / Type</th>
                          <th>Endpoint / Route</th>
                          <th>Frequency (24h)</th>
                          <th>Last Occurred</th>
                          <th>Status Control</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let errList = systemErrors || [];

                          if (errorFilterType === 'UNRESOLVED') {
                            errList = errList.filter(e => e.status === 'investigating' || !e.status);
                          } else if (errorFilterType === 'CRITICAL_500') {
                            errList = errList.filter(e => e.code && e.code.includes('500'));
                          } else if (errorFilterType === 'RESOLVED') {
                            errList = errList.filter(e => e.status === 'resolved');
                          }

                          if (errorSearchQuery.trim()) {
                            const q = errorSearchQuery.toLowerCase();
                            errList = errList.filter(e =>
                              (e.code && e.code.toLowerCase().includes(q)) ||
                              (e.endpoint && e.endpoint.toLowerCase().includes(q)) ||
                              (e.id && e.id.toLowerCase().includes(q))
                            );
                          }

                          if (errList.length === 0) {
                            return <tr><td colSpan="6" className="text-center" style={{ padding: '3rem', color: 'var(--text-muted)' }}>No system errors found matching filter.</td></tr>;
                          }
                          const paginated = errList.slice((systemErrorsPage - 1) * saItemsPerPage, systemErrorsPage * saItemsPerPage);
                          return paginated.map((err, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className={err.code.includes('500') ? "sa-badge-admin" : "sa-badge"} style={{ padding: '0.2rem 0.6rem', borderRadius: '4px', background: err.code.includes('500') ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)', color: err.code.includes('500') ? '#ef4444' : '#f59e0b', fontSize: '0.75rem', fontWeight: 700 }}>
                                  {err.code}
                                </span>
                              </td>
                              <td style={{ color: 'var(--text-main)', fontFamily: 'monospace', fontSize: '0.85rem' }}>{err.endpoint}</td>
                              <td>
                                <span style={{ fontWeight: 700, color: err.frequency > 5 ? '#ef4444' : 'var(--text-main)' }}>
                                  {err.frequency}x
                                </span>
                              </td>
                              <td className="sa-cell-muted" style={{ fontSize: '0.8rem' }}>{new Date(err.lastOccurred).toLocaleString()}</td>
                              <td>
                                <select
                                  value={err.status || 'investigating'}
                                  onChange={async (e) => {
                                    const newStatus = e.target.value;
                                    setSystemErrors(prev => (prev || []).map((item, i) => i === idx ? { ...item, status: newStatus } : item));
                                    showStatus(`Status marked as ${newStatus}`, 'success');
                                    try {
                                      await superAdminApi.updateSystemErrorStatus({ key: `${err.code}-${err.endpoint}`, status: newStatus });
                                    } catch (err) { }
                                  }}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border)',
                                    background: 'var(--surface)',
                                    color: err.status === 'resolved' ? '#10b981' : '#ef4444',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <option value="investigating">Unresolved</option>
                                  <option value="resolved">Resolved</option>
                                </select>
                              </td>
                              <td className="sa-actions-cell" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                <button
                                  className="sa-action-btn"
                                  title="View Error Details & Stack Trace"
                                  onClick={() => setSelectedErrorModal(err)}
                                  style={{ color: '#3b82f6', borderColor: 'rgba(59, 130, 246, 0.3)' }}
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  className="sa-action-btn"
                                  title={err.status === 'resolved' ? "Mark Unresolved" : "Mark Resolved"}
                                  onClick={async () => {
                                    const nextStatus = err.status === 'resolved' ? 'investigating' : 'resolved';
                                    setSystemErrors(prev => (prev || []).map((item) => item.id === err.id ? { ...item, status: nextStatus } : item));
                                    showStatus(`Status marked as ${nextStatus}`, 'success');
                                    try {
                                      await superAdminApi.updateSystemErrorStatus({ key: `${err.code}-${err.endpoint}`, status: nextStatus });
                                    } catch (e) { }
                                  }}
                                  style={{ color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)' }}
                                >
                                  <CheckSquare size={16} />
                                </button>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                    {renderPaginationControls(systemErrorsPage, Math.ceil((systemErrors || []).length / saItemsPerPage) || 1, (systemErrors || []).length, saItemsPerPage, setSystemErrorsPage, "errors")}
                  </div>

                  {/* Modal for Viewing Full Error Log Details */}
                  {selectedErrorModal && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1.5rem', backdropFilter: 'blur(4px)' }}>
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '16px', maxWidth: '650px', width: '100%', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)' }} className="fade-in">
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-hover)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Activity size={20} color="#ef4444" />
                            <div>
                              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-main)' }}>
                                {selectedErrorModal.code || 'System Error'}
                              </h3>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                ID: {selectedErrorModal.id || 'ERR-LOG'} • Occurred {new Date(selectedErrorModal.lastOccurred).toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <button onClick={() => setSelectedErrorModal(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.25rem' }}>
                            <X size={20} />
                          </button>
                        </div>
                        <div style={{ padding: '1.5rem', maxHeight: '70vh', overflowY: 'auto' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem', background: 'var(--bg-main)', padding: '1rem', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                            <div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Target Endpoint</div>
                              <div style={{ fontWeight: 600, color: 'var(--text-main)', marginTop: '0.2rem', fontFamily: 'monospace' }}>
                                {selectedErrorModal.endpoint}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>24h Frequency & Status</div>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.3rem' }}>
                                <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
                                  {selectedErrorModal.frequency} Triggered
                                </span>
                                <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, background: selectedErrorModal.status === 'resolved' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)', color: selectedErrorModal.status === 'resolved' ? '#10b981' : '#f59e0b' }}>
                                  {selectedErrorModal.status === 'resolved' ? 'Resolved' : 'Unresolved'}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.5rem' }}>Error Log Snippet & Stack Trace</div>
                            <div style={{ background: '#0f172a', padding: '1rem', borderRadius: '10px', border: '1px solid var(--border)', fontSize: '0.82rem', color: '#38bdf8', fontFamily: 'monospace', whiteSpace: 'pre-wrap', lineHeight: 1.5, overflowX: 'auto' }}>
                              {selectedErrorModal.logSnippet
                                ? `[SERVER_LOG_EVENT] ${selectedErrorModal.code} at ${selectedErrorModal.endpoint}\n\n${selectedErrorModal.logSnippet}\n\nTimestamp: ${selectedErrorModal.lastOccurred}\nFrequency: ${selectedErrorModal.frequency} occurrences\nStatus: ${selectedErrorModal.status || 'investigating'}`
                                : `[SERVER_LOG_EVENT] ${selectedErrorModal.code} at ${selectedErrorModal.endpoint}\nTimestamp: ${selectedErrorModal.lastOccurred}\nFrequency: ${selectedErrorModal.frequency} occurrences\nStatus: ${selectedErrorModal.status || 'investigating'}\n\nStack Trace:\n  at RouteHandler (${selectedErrorModal.endpoint}:14:23)\n  at Layer.handle [as handle_request] (express/lib/router/layer.js:95:5)\n  at Next (express/lib/router/route.js:144:13)`
                              }
                            </div>
                          </div>
                        </div>

                        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-hover)' }}>
                          <button
                            className="sa-action-btn"
                            style={{ background: 'var(--primary)', color: '#fff', padding: '0.4rem 1rem' }}
                            onClick={async () => {
                              const newStatus = selectedErrorModal.status === 'resolved' ? 'investigating' : 'resolved';
                              setSystemErrors(prev => (prev || []).map(e => e.id === selectedErrorModal.id ? { ...e, status: newStatus } : e));
                              setSelectedErrorModal(prev => ({ ...prev, status: newStatus }));
                              showStatus(`Status set to ${newStatus}`, 'success');
                              try {
                                await superAdminApi.updateSystemErrorStatus({ key: `${selectedErrorModal.code}-${selectedErrorModal.endpoint}`, status: newStatus });
                              } catch (e) { }
                            }}
                          >
                            <CheckSquare size={16} /> {selectedErrorModal.status === 'resolved' ? 'Mark Unresolved' : 'Mark Resolved'}
                          </button>
                          <button className="sa-action-btn" onClick={() => setSelectedErrorModal(null)}>Close</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Support Tickets Section */}
              {activeSection === 'support' && (
                <div className="sa-section fade-in">
                  <div className="dashboard-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div className="dashboard-header-title-area">
                      <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--text-main)' }}>
                        <FileText size={22} color="var(--primary)" /> Support Tickets Management
                        <span style={{ fontSize: '0.78rem', background: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary)', padding: '0.2rem 0.65rem', borderRadius: '12px', fontWeight: 600 }}>
                          {(supportTickets || []).length} Total
                        </span>
                      </h2>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem', margin: 0 }}>
                        User support inquiries, and technical issue tracking.
                      </p>
                    </div>
                    <button className="sa-action-btn" style={{ padding: '0.5rem 0.9rem', height: 'auto', background: 'var(--surface)', color: 'var(--text-main)', borderColor: 'var(--border)' }} onClick={async () => {
                      try {
                        const ticketsRes = await superAdminApi.getSupportTickets();
                        setSupportTickets(ticketsRes.tickets || []);
                        showStatus('Support tickets refreshed', 'success');
                      } catch (e) {
                        showStatus('Unable to refresh tickets list', 'info');
                      }
                    }}>
                      <RefreshCw size={14} /> Refresh List
                    </button>
                  </div>

                  {/* Summary Metric Cards */}
                  <div className="premium-stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', margin: '0 1.5rem 1.5rem' }}>
                    <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>Total Inquiries</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>{(supportTickets || []).length}</div>
                    </div>
                    <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>Open Tickets</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ef4444', marginTop: '0.2rem' }}>
                        {(supportTickets || []).filter(t => t.status === 'OPEN' || !t.status).length}
                      </div>
                    </div>
                    <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>In Progress</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f59e0b', marginTop: '0.2rem' }}>
                        {(supportTickets || []).filter(t => t.status === 'IN_PROGRESS').length}
                      </div>
                    </div>
                    <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>Resolved</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981', marginTop: '0.2rem' }}>
                        {(supportTickets || []).filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED').length}
                      </div>
                    </div>
                  </div>

                  {/* Filter & Search Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', margin: '0 1.5rem 1.5rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'inline-flex', background: 'var(--surface-hover, rgba(0, 0, 0, 0.04))', padding: '0.3rem', borderRadius: '12px', border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))', gap: '0.25rem' }}>
                      {['ALL', 'OPEN', 'IN_PROGRESS', 'RESOLVED'].map(tabKey => (
                        <button
                          key={tabKey}
                          onClick={() => { setTicketStatusFilter(tabKey); setSupportTicketsPage(1); }}
                          style={{
                            padding: '0.4rem 0.85rem',
                            borderRadius: '8px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            background: ticketStatusFilter === tabKey ? 'var(--surface)' : 'transparent',
                            color: ticketStatusFilter === tabKey ? 'var(--primary)' : 'var(--text-muted)',
                            boxShadow: ticketStatusFilter === tabKey ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                          }}
                        >
                          {tabKey === 'ALL' ? 'All Tickets' : tabKey === 'OPEN' ? 'Open' : tabKey === 'IN_PROGRESS' ? 'In Progress' : 'Resolved'}
                        </button>
                      ))}
                    </div>
                    <div style={{ position: 'relative', width: '280px' }}>
                      <Search size={16} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        type="text"
                        placeholder="Search tickets or users..."
                        value={ticketSearchQuery}
                        onChange={e => { setTicketSearchQuery(e.target.value); setSupportTicketsPage(1); }}
                        style={{
                          width: '100%',
                          padding: '0.45rem 0.85rem 0.45rem 2.2rem',
                          borderRadius: '10px',
                          border: '1px solid var(--border)',
                          background: 'var(--surface)',
                          color: 'var(--text-main)',
                          fontSize: '0.85rem'
                        }}
                      />
                    </div>
                  </div>

                  <div className="sa-table-wrapper" style={{ margin: '0 1.5rem 1.5rem' }}>
                    <table className="sa-table">
                      <thead>
                        <tr>
                          <th>Ticket ID</th>
                          <th>Submitted By</th>
                          <th>Subject / Details</th>
                          <th>Created Date</th>
                          <th>Priority</th>
                          <th>Status Control</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let tList = supportTickets || [];

                          if (ticketStatusFilter !== 'ALL') {
                            tList = tList.filter(t => (t.status || 'OPEN') === ticketStatusFilter);
                          }

                          if (ticketSearchQuery.trim()) {
                            const q = ticketSearchQuery.toLowerCase();
                            tList = tList.filter(t =>
                              (t.publicId && t.publicId.toLowerCase().includes(q)) ||
                              (t.subject && t.subject.toLowerCase().includes(q)) ||
                              (t.description && t.description.toLowerCase().includes(q)) ||
                              (t.client?.fullName && t.client.fullName.toLowerCase().includes(q)) ||
                              (t.client?.email && t.client.email.toLowerCase().includes(q)) ||
                              (t.user?.username && t.user.username.toLowerCase().includes(q)) ||
                              (t.user?.email && t.user.email.toLowerCase().includes(q))
                            );
                          }

                          if (tList.length === 0) {
                            return <tr><td colSpan="7" className="text-center" style={{ padding: '3rem', color: 'var(--text-muted)' }}>No support tickets found matching your filter.</td></tr>;
                          }
                          const paginated = tList.slice((supportTicketsPage - 1) * saItemsPerPage, supportTicketsPage * saItemsPerPage);
                          return paginated.map((ticket, idx) => (
                            <tr key={idx}>
                              <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.82rem' }}>#{ticket.publicId || ticket.id.substring(0, 8)}</td>
                              <td>
                                <div className="sa-cell-with-icon">
                                  <div className="sa-org-avatar" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', width: '32px', height: '32px', fontSize: '0.85rem' }}>
                                    {ticket.client?.fullName ? ticket.client.fullName.substring(0, 2).toUpperCase() : (ticket.user?.username ? ticket.user.username.substring(0, 2).toUpperCase() : 'U')}
                                  </div>
                                  <div>
                                    <div className="sa-cell-bold">{ticket.client?.fullName || ticket.user?.username || 'Unknown User'}</div>
                                    <div className="sa-cell-muted">{ticket.client?.email || ticket.user?.email || 'N/A'}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ color: 'var(--text-main)' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{ticket.subject}</div>
                                <div className="sa-cell-muted" style={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px' }}>
                                  {ticket.description || 'No description provided'}
                                </div>
                              </td>
                              <td>
                                <div style={{ fontSize: '0.82rem', color: 'var(--text-main)', fontWeight: 500 }}>
                                  {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                                </div>
                                <div className="sa-cell-muted" style={{ fontSize: '0.72rem' }}>
                                  {ticket.createdAt ? new Date(ticket.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                                </div>
                              </td>
                              <td>
                                <span style={{
                                  padding: '0.2rem 0.6rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  background: ticket.priority === 'HIGH' || ticket.priority === 'URGENT' ? 'rgba(239, 68, 68, 0.15)' : ticket.priority === 'LOW' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                                  color: ticket.priority === 'HIGH' || ticket.priority === 'URGENT' ? '#ef4444' : ticket.priority === 'LOW' ? '#4ade80' : '#f59e0b'
                                }}>
                                  {ticket.priority || 'MEDIUM'}
                                </span>
                              </td>
                              <td>
                                <select
                                  value={ticket.status || 'OPEN'}
                                  onChange={async (e) => {
                                    const newStatus = e.target.value;
                                    try {
                                      await api.updateTicket(ticket.id, { status: newStatus });
                                      showStatus(`Ticket status updated to ${newStatus}`, 'success');
                                      const ticketsRes = await superAdminApi.getSupportTickets();
                                      setSupportTickets(ticketsRes.tickets || []);
                                    } catch (err) {
                                      showStatus('Failed to update status: ' + err.message, 'error');
                                    }
                                  }}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border)',
                                    background: 'var(--surface)',
                                    color: ticket.status === 'OPEN' ? '#ef4444' : ticket.status === 'IN_PROGRESS' ? '#f59e0b' : '#10b981',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <option value="OPEN">Open</option>
                                  <option value="IN_PROGRESS">In Progress</option>
                                  <option value="RESOLVED">Resolved</option>
                                  <option value="CLOSED">Closed</option>
                                </select>
                              </td>
                              <td className="sa-actions-cell" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                <button
                                  className="sa-action-btn"
                                  title="View Ticket Details"
                                  onClick={() => setSelectedTicketModal(ticket)}
                                  style={{ color: '#3b82f6', borderColor: 'rgba(59, 130, 246, 0.3)' }}
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  className="sa-action-btn"
                                  title="Delete Ticket"
                                  onClick={async () => {
                                    if (window.confirm(`Delete ticket #${ticket.publicId || ticket.id}? This cannot be undone.`)) {
                                      try {
                                        await api.deleteTicket(ticket.id);
                                        showStatus('Ticket deleted successfully', 'success');
                                        const ticketsRes = await superAdminApi.getSupportTickets();
                                        setSupportTickets(ticketsRes.tickets || []);
                                      } catch (err) {
                                        showStatus('Failed to delete ticket: ' + err.message, 'error');
                                      }
                                    }
                                  }}
                                  style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                    {renderPaginationControls(supportTicketsPage, Math.ceil((supportTickets || []).length / saItemsPerPage) || 1, (supportTickets || []).length, saItemsPerPage, setSupportTicketsPage, "tickets")}
                  </div>

                  {/* Modal for Viewing Full Ticket Details */}
                  {selectedTicketModal && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1.5rem', backdropFilter: 'blur(4px)' }}>
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '16px', maxWidth: '650px', width: '100%', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)' }} className="fade-in">
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-hover)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <FileText size={20} color="var(--primary)" />
                            <div>
                              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-main)' }}>
                                Ticket #{selectedTicketModal.publicId || selectedTicketModal.id.substring(0, 8)}
                              </h3>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                Created {selectedTicketModal.createdAt ? new Date(selectedTicketModal.createdAt).toLocaleString() : 'Recently'}
                              </span>
                            </div>
                          </div>
                          <button onClick={() => setSelectedTicketModal(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.25rem' }}>
                            <X size={20} />
                          </button>
                        </div>
                        <div style={{ padding: '1.5rem', maxHeight: '70vh', overflowY: 'auto' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem', background: 'var(--bg-main)', padding: '1rem', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                            <div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Submitted By</div>
                              <div style={{ fontWeight: 600, color: 'var(--text-main)', marginTop: '0.2rem' }}>
                                {selectedTicketModal.client?.fullName || selectedTicketModal.user?.username || 'Unknown User'}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                {selectedTicketModal.client?.email || selectedTicketModal.user?.email || 'N/A'}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Priority & Status</div>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.3rem' }}>
                                <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                                  {selectedTicketModal.priority || 'MEDIUM'}
                                </span>
                                <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, background: selectedTicketModal.status === 'OPEN' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)', color: selectedTicketModal.status === 'OPEN' ? '#ef4444' : '#10b981' }}>
                                  {selectedTicketModal.status || 'OPEN'}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.3rem' }}>Subject</div>
                            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-main)' }}>{selectedTicketModal.subject}</div>
                          </div>

                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.5rem' }}>Issue Description / Details</div>
                            <div style={{ background: 'var(--bg-main)', padding: '1rem', borderRadius: '10px', border: '1px solid var(--border)', fontSize: '0.9rem', color: 'var(--text-main)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                              {selectedTicketModal.description || 'No detailed message provided for this ticket.'}
                            </div>
                          </div>
                        </div>

                        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-hover)' }}>
                          <button
                            className="sa-action-btn"
                            style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                            onClick={async () => {
                              if (window.confirm('Delete this ticket?')) {
                                try {
                                  await api.deleteTicket(selectedTicketModal.id);
                                  showStatus('Ticket deleted', 'success');
                                  setSelectedTicketModal(null);
                                  const ticketsRes = await superAdminApi.getSupportTickets();
                                  setSupportTickets(ticketsRes.tickets || []);
                                } catch (err) {
                                  showStatus('Failed to delete: ' + err.message, 'error');
                                }
                              }
                            }}
                          >
                            <Trash2 size={16} /> Delete Ticket
                          </button>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              className="sa-action-btn"
                              style={{ background: 'var(--primary)', color: '#fff', padding: '0.4rem 1rem' }}
                              onClick={async () => {
                                try {
                                  const newStatus = selectedTicketModal.status === 'RESOLVED' ? 'OPEN' : 'RESOLVED';
                                  await api.updateTicket(selectedTicketModal.id, { status: newStatus });
                                  showStatus(`Ticket set to ${newStatus}`, 'success');
                                  setSelectedTicketModal(prev => ({ ...prev, status: newStatus }));
                                  const ticketsRes = await superAdminApi.getSupportTickets();
                                  setSupportTickets(ticketsRes.tickets || []);
                                } catch (err) {
                                  showStatus('Failed to update ticket', 'error');
                                }
                              }}
                            >
                              <CheckSquare size={16} /> {selectedTicketModal.status === 'RESOLVED' ? 'Reopen Ticket' : 'Mark as Resolved'}
                            </button>
                            <button className="sa-action-btn" onClick={() => setSelectedTicketModal(null)}>Close</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}


              {activeSection === 'domains' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <h2><Globe size={24} strokeWidth={2.0} /> Custom Domains</h2>
                    <p className="subtitle">Global view of all user custom domains</p>
                  </div>
                  <div className="sa-card">
                    <div className="table-responsive">
                      <table className="sa-table">
                        <thead>
                          <tr>
                            <th>Domain</th>
                            <th>Owner</th>
                            <th>App</th>
                            <th>Port</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const domList = globalDomains || [];
                            if (domList.length === 0) {
                              return <tr><td colSpan="4" className="text-center">No custom domains configured</td></tr>;
                            }
                            const paginated = domList.slice((globalDomainsPage - 1) * saItemsPerPage, globalDomainsPage * saItemsPerPage);
                            return paginated.map((d, idx) => (
                              <tr key={idx}>
                                <td><a href={`http://${d.domain}`} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{d.domain}</a></td>
                                <td><span className="sa-badge sa-badge-outline">{d.owner}</span></td>
                                <td>{d.appName}</td>
                                <td style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                  {d.port}
                                  <button className="sa-action-btn sa-action-delete" title="Revoke Domain" onClick={async () => {
                                    if (window.confirm(`Revoke custom domain ${d.domain}?`)) {
                                      try {
                                        await superAdminApi.revokeDomain(d.domain);
                                        showStatus('Domain revoked', 'success');
                                        const doms = await superAdminApi.getGlobalDomains();
                                        setGlobalDomains(doms.domains || []);
                                      } catch (e) {
                                        showStatus('Failed to revoke domain: ' + e.message, 'error');
                                      }
                                    }
                                  }}><Trash2 size={16} /></button>
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                    {renderPaginationControls(globalDomainsPage, Math.ceil((globalDomains || []).length / saItemsPerPage) || 1, (globalDomains || []).length, saItemsPerPage, setGlobalDomainsPage, "domains")}
                  </div>
                </div>
              )}

              {activeSection === 'serverlogs' && <SystemLogsSection />}

              {activeSection === 'gitconnections' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header" style={{ marginBottom: '0.75rem' }}>
                    <h2><GitBranch size={24} strokeWidth={2.0} /> Git Providers</h2>
                    <p className="subtitle">Active OAuth connections across the platform</p>
                  </div>
                  <div className="sa-stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div className="sa-stat-card" onClick={() => setActiveGitTab('git')} style={{ cursor: 'pointer', border: activeGitTab === 'git' ? '1px solid var(--primary)' : undefined }}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                        <GitBranch size={22} />
                      </div>
                      <div className="sa-stat-info">
                        <div className="sa-stat-value">{gitConnectionsSummary?.totalGithub ?? 0}</div>
                        <div className="sa-stat-label">GitHub connections</div>
                      </div>
                    </div>
                    <div className="sa-stat-card" onClick={() => setActiveGitTab('git')} style={{ cursor: 'pointer', border: activeGitTab === 'git' ? '1px solid var(--primary)' : undefined }}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }}>
                        <GitBranch size={22} />
                      </div>
                      <div className="sa-stat-info">
                        <div className="sa-stat-value">{gitConnectionsSummary?.totalGitlab ?? 0}</div>
                        <div className="sa-stat-label">GitLab connections</div>
                      </div>
                    </div>
                    <div className="sa-stat-card" onClick={() => setActiveGitTab('manual')} style={{ cursor: 'pointer', border: activeGitTab === 'manual' ? '1px solid var(--primary)' : undefined }}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}>
                        <Users size={22} />
                      </div>
                      <div className="sa-stat-info">
                        <div className="sa-stat-value">{gitConnectionsSummary?.manualSignupUsers ?? 0}</div>
                        <div className="sa-stat-label">Manual signups</div>
                      </div>
                    </div>
                    <div className="sa-stat-card" onClick={() => setActiveGitTab('git')} style={{ cursor: 'pointer' }}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}>
                        <ShieldCheck size={22} />
                      </div>
                      <div className="sa-stat-info">
                        <div className="sa-stat-value">{gitConnectionsSummary?.verifiedUsers ?? 0}</div>
                        <div className="sa-stat-label">Verified users</div>
                      </div>
                    </div>
                    <div className="sa-stat-card" onClick={() => setActiveGitTab('manual')} style={{ cursor: 'pointer' }}>
                      <div className="sa-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                        <ShieldAlert size={22} />
                      </div>
                      <div className="sa-stat-info">
                        <div className="sa-stat-value">{gitConnectionsSummary?.unverifiedUsers ?? 0}</div>
                        <div className="sa-stat-label">Unverified users</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className={`sa-btn ${activeGitTab === 'git' ? 'sa-btn-primary' : 'sa-btn-secondary'}`}
                        onClick={() => { setActiveGitTab('git'); setGitConnectionsPage(1); }}
                      >
                        <GitBranch size={16} /> Git Connections ({(gitConnections || []).length})
                      </button>
                      <button
                        className={`sa-btn ${activeGitTab === 'manual' ? 'sa-btn-primary' : 'sa-btn-secondary'}`}
                        onClick={() => { setActiveGitTab('manual'); setManualUsersPage(1); }}
                      >
                        <Users size={16} /> Manual Signups ({(gitConnectionsSummary?.manualUsers || []).length})
                      </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {/* Search Input */}
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', margin: 0, padding: '0 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-main)', height: '36px', width: '140px', boxSizing: 'border-box' }}>
                        <Search size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        <input
                          type="text"
                          placeholder="Search..."
                          value={gitConnectionsSearch}
                          onChange={(e) => {
                            setGitConnectionsSearch(e.target.value);
                            setGitConnectionsPage(1);
                            setManualUsersPage(1);
                          }}
                          style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-main)', fontSize: '0.85rem', width: '100%', minWidth: 0, height: '100%' }}
                        />
                      </div>

                      {/* Filter Dropdown */}
                      {activeGitTab === 'git' ? (
                        <select
                          className="sa-filter-select"
                          value={gitProviderFilter}
                          onChange={(e) => {
                            setGitProviderFilter(e.target.value);
                            setGitConnectionsPage(1);
                          }}
                          style={{ fontSize: '0.85rem', height: '36px', width: '140px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', cursor: 'pointer', boxSizing: 'border-box' }}
                        >
                          <option value="all">All Providers</option>
                          <option value="github">GitHub</option>
                          <option value="gitlab">GitLab</option>
                        </select>
                      ) : (
                        <select
                          className="sa-filter-select"
                          value={manualStatusFilter}
                          onChange={(e) => {
                            setManualStatusFilter(e.target.value);
                            setManualUsersPage(1);
                          }}
                          style={{ fontSize: '0.85rem', height: '36px', width: '140px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', cursor: 'pointer', boxSizing: 'border-box' }}
                        >
                          <option value="all">All Statuses</option>
                          <option value="verified">Verified Only</option>
                          <option value="unverified">Unverified Only</option>
                        </select>
                      )}
                    </div>
                  </div>

                  {activeGitTab === 'git' ? (
                    <div className="sa-card" style={{ padding: '1rem' }}>
                      <div className="table-responsive">
                        <table className="sa-table">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Provider</th>
                              <th>Connected At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rawGitList = gitConnections || [];
                              const gitList = rawGitList.filter(c => {
                                const searchLower = gitConnectionsSearch.toLowerCase();
                                const matchesSearch = !gitConnectionsSearch || (c.username && c.username.toLowerCase().includes(searchLower));
                                const matchesProvider = gitProviderFilter === 'all' || c.provider === gitProviderFilter;
                                return matchesSearch && matchesProvider;
                              });

                              if (gitList.length === 0) {
                                return <tr><td colSpan="3" className="text-center">No Git connections found</td></tr>;
                              }
                              const paginated = gitList.slice((gitConnectionsPage - 1) * saItemsPerPage, gitConnectionsPage * saItemsPerPage);
                              return paginated.map((c, idx) => (
                                <tr key={idx}>
                                  <td style={{ fontWeight: 500 }}>{c.username}</td>
                                  <td><span className={`sa-badge sa-badge-${c.provider === 'github' ? 'success' : 'info'}`} style={{ textTransform: 'capitalize' }}>{c.provider}</span></td>
                                  <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    {new Date(c.createdAt).toLocaleString()}
                                    <button className="sa-action-btn sa-action-delete" title="Disconnect Git Provider" onClick={async () => {
                                      if (window.confirm(`Disconnect ${c.provider} for ${c.username}?`)) {
                                        try {
                                          await superAdminApi.revokeGitConnection(c.id);
                                          showStatus('Git connection revoked', 'success');
                                          const gits = await superAdminApi.getGitConnections();
                                          setGitConnections(gits.connections || []);
                                          setGitConnectionsSummary(gits);
                                        } catch (e) {
                                          showStatus('Failed to revoke git connection: ' + e.message, 'error');
                                        }
                                      }
                                    }}><Trash2 size={16} /></button>
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                        {(() => {
                          const filteredCount = (gitConnections || []).filter(c => {
                            const searchLower = gitConnectionsSearch.toLowerCase();
                            const matchesSearch = !gitConnectionsSearch || (c.username && c.username.toLowerCase().includes(searchLower));
                            const matchesProvider = gitProviderFilter === 'all' || c.provider === gitProviderFilter;
                            return matchesSearch && matchesProvider;
                          }).length;
                          return renderPaginationControls(gitConnectionsPage, Math.ceil(filteredCount / saItemsPerPage) || 1, filteredCount, saItemsPerPage, setGitConnectionsPage, "connections");
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div className="sa-card" style={{ padding: '1rem' }}>
                      <div className="table-responsive">
                        <table className="sa-table">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Connected At</th>
                              <th>Verification status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rawManualList = gitConnectionsSummary?.manualUsers || [];
                              const manualList = rawManualList.filter(u => {
                                const searchLower = gitConnectionsSearch.toLowerCase();
                                const matchesSearch = !gitConnectionsSearch ||
                                  (u.username && u.username.toLowerCase().includes(searchLower)) ||
                                  (u.email && u.email.toLowerCase().includes(searchLower));
                                const matchesStatus = manualStatusFilter === 'all' ||
                                  (manualStatusFilter === 'verified' && u.emailVerified) ||
                                  (manualStatusFilter === 'unverified' && !u.emailVerified);
                                return matchesSearch && matchesStatus;
                              });

                              if (manualList.length === 0) {
                                return <tr><td colSpan="3" className="text-center">No manual signups found</td></tr>;
                              }
                              const paginated = manualList.slice((manualUsersPage - 1) * saItemsPerPage, manualUsersPage * saItemsPerPage);
                              return paginated.map((u, idx) => (
                                <tr key={u.id || idx}>
                                  <td>
                                    <div className="sa-cell-with-icon">
                                      <div className="sa-org-avatar" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
                                        {u.username.substring(0, 2).toUpperCase()}
                                      </div>
                                      <div>
                                        <div className="sa-cell-bold">{u.username}</div>
                                        <div className="sa-cell-muted">{u.email || 'No email'}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ fontWeight: 500 }}>
                                    {u.createdAt ? new Date(u.createdAt).toLocaleString() : 'N/A'}
                                  </td>
                                  <td>
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        padding: '0.15rem 0.5rem',
                                        borderRadius: '12px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        background: u.emailVerified ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.15)',
                                        color: u.emailVerified ? '#22c55e' : '#f59e0b'
                                      }}
                                    >
                                      {u.emailVerified ? 'Verified' : 'Unverified'}
                                    </span>
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                        {(() => {
                          const filteredCount = (gitConnectionsSummary?.manualUsers || []).filter(u => {
                            const searchLower = gitConnectionsSearch.toLowerCase();
                            const matchesSearch = !gitConnectionsSearch ||
                              (u.username && u.username.toLowerCase().includes(searchLower)) ||
                              (u.email && u.email.toLowerCase().includes(searchLower));
                            const matchesStatus = manualStatusFilter === 'all' ||
                              (manualStatusFilter === 'verified' && u.emailVerified) ||
                              (manualStatusFilter === 'unverified' && !u.emailVerified);
                            return matchesSearch && matchesStatus;
                          }).length;
                          return renderPaginationControls(manualUsersPage, Math.ceil(filteredCount / saItemsPerPage) || 1, filteredCount, saItemsPerPage, setManualUsersPage, "manual signups");
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeSection === 'audit' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <h2><Activity size={24} strokeWidth={2.0} /> System Audit Logs</h2>

                  </div>

                  {/* Audit Summary Metrics Grid */}
                  <div className="sa-premium-stats-grid" style={{ marginBottom: '1.5rem' }}>
                    <div className="sa-premium-stat-card">
                      <div className="sa-premium-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                        <FileText size={24} />
                      </div>
                      <div className="sa-premium-stat-details">
                        <div className="sa-premium-stat-label">Total logs</div>
                        <div className="sa-premium-stat-value">
                          {isAuditSummaryLoading ? <Loader2 size={16} className="sa-spin" /> : (auditSummary?.totalLogs || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="sa-premium-stat-card" style={{ borderColor: 'rgba(239, 68, 68, 0.3)' }}>
                      <div className="sa-premium-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
                        <ShieldAlert size={24} />
                      </div>
                      <div className="sa-premium-stat-details">
                        <div className="sa-premium-stat-label" style={{ color: '#ef4444' }}>Critical events</div>
                        <div className="sa-premium-stat-value" style={{ color: '#ef4444' }}>
                          {isAuditSummaryLoading ? <Loader2 size={16} className="sa-spin" /> : (auditSummary?.severityCounts?.critical || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="sa-premium-stat-card" style={{ borderColor: 'rgba(245, 158, 11, 0.3)' }}>
                      <div className="sa-premium-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                        <AlertCircle size={24} />
                      </div>
                      <div className="sa-premium-stat-details">
                        <div className="sa-premium-stat-label" style={{ color: '#f59e0b' }}>Warnings</div>
                        <div className="sa-premium-stat-value" style={{ color: '#f59e0b' }}>
                          {isAuditSummaryLoading ? <Loader2 size={16} className="sa-spin" /> : (auditSummary?.severityCounts?.warning || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="sa-premium-stat-card">
                      <div className="sa-premium-stat-icon" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
                        <Globe size={24} />
                      </div>
                      <div className="sa-premium-stat-details">
                        <div className="sa-premium-stat-label">Unique IPs</div>
                        <div className="sa-premium-stat-value">
                          {isAuditSummaryLoading ? <Loader2 size={16} className="sa-spin" /> : (auditSummary?.uniqueIpCount || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top Actions & Suspicious Activity Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div className="sa-card" style={{ margin: 0, padding: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Activity size={18} style={{ color: 'var(--primary)' }} /> Top action categories
                        </h3>
                      </div>
                      {(() => {
                        const topActions = auditSummary?.topActions || [];
                        if (topActions.length === 0) {
                          return <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '0.75rem 0' }}>No action categories recorded yet.</div>;
                        }
                        const maxCount = Math.max(...topActions.map(a => a.count || 1), 1);
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                            {topActions.map((item, idx) => {
                              const pct = Math.min(100, Math.max(8, (item.count / maxCount) * 100));
                              return (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                    <span style={{ fontWeight: 500, color: 'var(--text-main)' }}>{item.action}</span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{item.count}</span>
                                  </div>
                                  <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', borderRadius: '3px', transition: 'width 0.4s ease' }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="sa-card" style={{ margin: 0, padding: '1.25rem', borderStyle: 'dashed', background: 'rgba(245, 158, 11, 0.03)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                        <div style={{ padding: '0.5rem', borderRadius: '8px', background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', flexShrink: 0 }}>
                          <AlertTriangle size={22} />
                        </div>
                        <div>
                          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.4rem 0', color: '#f59e0b' }}>
                            Suspicious activity
                          </h3>
                          <p style={{ fontSize: '0.825rem', color: 'var(--text-muted)', lineHeight: '1.45', margin: 0 }}>
                            No detection rule defined yet — needs a threshold agreed with the team (e.g. repeated failed logins from one IP).
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="sa-card">
                    {/* Modern Data Table Filter Toolbar */}
                    <div style={{
                      padding: '1rem 1.25rem',
                      marginBottom: '1.25rem',
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.85rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Filter size={16} style={{ color: 'var(--primary, #6366f1)' }} /> Filter Audit Logs
                        </span>
                        {(auditFilterSeverity || auditFilterAction || auditFilterDate || auditSearchQuery) && (
                          <button
                            onClick={() => {
                              setAuditFilterSeverity('');
                              setAuditFilterAction('');
                              setAuditFilterDate('');
                              setAuditSearchQuery('');
                              setAuditPage(1);
                            }}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#ef4444',
                              border: '1px solid rgba(239, 68, 68, 0.25)',
                              padding: '0.3rem 0.65rem',
                              borderRadius: '6px',
                              fontSize: '0.775rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <RotateCcw size={12} /> Reset Filters
                          </button>
                        )}
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '0.75rem',
                        alignItems: 'center'
                      }}>
                        {/* 1. Severities Dropdown */}
                        <div style={{ position: 'relative' }}>
                          <select
                            value={auditFilterSeverity}
                            onChange={(e) => {
                              setAuditFilterSeverity(e.target.value);
                              setAuditPage(1);
                            }}
                            className="sa-input"
                            style={{ width: '100%', borderRadius: '8px', paddingRight: '2rem', appearance: 'none', background: 'var(--surface, #1e293b)', cursor: 'pointer', fontSize: '0.85rem' }}
                          >
                            <option value="">All severities</option>
                            <option value="info">info</option>
                            <option value="warning">warning</option>
                            <option value="critical">critical</option>
                          </select>
                          <ChevronDown size={14} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                        </div>

                        {/* 2. Actions Dropdown */}
                        <div style={{ position: 'relative' }}>
                          <select
                            value={auditFilterAction}
                            onChange={(e) => {
                              setAuditFilterAction(e.target.value);
                              setAuditPage(1);
                            }}
                            className="sa-input"
                            style={{ width: '100%', borderRadius: '8px', paddingRight: '2rem', appearance: 'none', background: 'var(--surface, #1e293b)', cursor: 'pointer', fontSize: '0.85rem' }}
                          >
                            <option value="">All actions</option>
                            {(() => {
                              const realActions = Array.from(new Set((auditLogs || []).map(l => l.action).filter(Boolean))).sort();
                              return realActions.map(act => (
                                <option key={act} value={act}>{act}</option>
                              ));
                            })()}
                          </select>
                          <ChevronDown size={14} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                        </div>

                        {/* 3. Date Picker Input */}
                        <div style={{ position: 'relative' }}>
                          <Calendar size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: auditFilterDate ? 'var(--primary, #6366f1)' : 'var(--text-muted)', pointerEvents: 'none', zIndex: 2 }} />
                          <input
                            type={auditDateFocused || auditFilterDate ? "date" : "text"}
                            placeholder="mm/dd/yyyy"
                            value={auditFilterDate}
                            onFocus={() => setAuditDateFocused(true)}
                            onBlur={() => setAuditDateFocused(false)}
                            onChange={(e) => {
                              setAuditFilterDate(e.target.value);
                              setAuditPage(1);
                            }}
                            className="sa-input"
                            style={{
                              width: '100%',
                              paddingLeft: '2.4rem',
                              borderRadius: '8px',
                              background: 'var(--surface, #1e293b)',
                              color: 'var(--text-main, #f8fafc)',
                              colorScheme: 'dark',
                              fontSize: '0.85rem'
                            }}
                          />
                        </div>

                        {/* 4. Text Search Bar */}
                        <div style={{ position: 'relative' }}>
                          <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                          <input
                            type="text"
                            placeholder="search by actor or IP..."
                            value={auditSearchQuery}
                            onChange={(e) => {
                              setAuditSearchQuery(e.target.value);
                              setAuditPage(1);
                            }}
                            className="sa-input"
                            style={{ width: '100%', paddingLeft: '2.4rem', paddingRight: auditSearchQuery ? '2.2rem' : '0.85rem', borderRadius: '8px', background: 'var(--surface, #1e293b)', fontSize: '0.85rem' }}
                          />
                          {auditSearchQuery && (
                            <button
                              onClick={() => { setAuditSearchQuery(''); setAuditPage(1); }}
                              style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <table className="sa-table">
                        <thead>
                          <tr>
                            <th>Timestamp</th>
                            <th>Actor</th>
                            <th>Action & Details</th>
                            <th>IP Address</th>
                            <th style={{ textAlign: 'right' }}>Severity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const rawList = auditLogs || [];

                            const filteredList = rawList.filter(log => {
                              // 1. Severities filter
                              if (auditFilterSeverity) {
                                const logSev = (log.severity || '').toLowerCase();
                                const targetSev = auditFilterSeverity.toLowerCase();
                                if (targetSev === 'critical' && !(logSev === 'critical' || logSev === 'high' || logSev === 'danger')) return false;
                                if (targetSev === 'warning' && !(logSev === 'warning' || logSev === 'medium' || logSev === 'warn')) return false;
                                if (targetSev === 'info' && !(logSev === 'info' || logSev === 'low')) return false;
                                if (targetSev !== 'critical' && targetSev !== 'warning' && targetSev !== 'info' && logSev !== targetSev) return false;
                              }

                              // 2. Actions filter
                              if (auditFilterAction) {
                                if ((log.action || '').toLowerCase() !== auditFilterAction.toLowerCase()) return false;
                              }

                              // 3. Date picker filter
                              if (auditFilterDate && log.timestamp) {
                                const logDateStr = new Date(log.timestamp).toISOString().split('T')[0];
                                if (logDateStr !== auditFilterDate) return false;
                              }

                              // 4. Text search filter ("search by actor or IP")
                              if (auditSearchQuery) {
                                const q = auditSearchQuery.toLowerCase().trim();
                                const actor = (log.actorUsername || log.username || '').toLowerCase();
                                const ip = (log.ipAddress || '').toLowerCase();
                                const action = (log.action || '').toLowerCase();
                                const details = log.details ? (typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details)).toLowerCase() : '';
                                if (!actor.includes(q) && !ip.includes(q) && !action.includes(q) && !details.includes(q)) return false;
                              }

                              return true;
                            });

                            if (filteredList.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="5" className="text-center" style={{ padding: '2rem', color: 'var(--text-muted)' }}>
                                    No audit logs matching selected filters.
                                  </td>
                                </tr>
                              );
                            }
                            const paginated = filteredList.slice((auditPage - 1) * saItemsPerPage, auditPage * saItemsPerPage);
                            return paginated.map((log, idx) => (
                              <tr key={idx}>
                                <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{new Date(log.timestamp).toLocaleString()}</td>
                                <td>
                                  <div className="sa-cell-with-icon">
                                    <div className="sa-org-avatar" style={{ background: log.actorUsername ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'linear-gradient(135deg, #64748b, #475569)', width: '28px', height: '28px', fontSize: '0.7rem' }}>
                                      {(log.actorUsername || 'SYS').substring(0, 3).toUpperCase()}
                                    </div>
                                    <div className="sa-cell-bold">{log.actorUsername || 'System Account'}</div>
                                  </div>
                                </td>
                                <td>
                                  <div style={{ color: 'var(--text-main)', fontWeight: 500 }}>{log.action}</div>
                                  {log.details && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}</div>}
                                </td>
                                <td>
                                  {log.ipAddress ? <span className="code-text" style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}>{log.ipAddress}</span> : <span style={{ color: 'var(--text-tertiary)' }}>N/A</span>}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {(() => {
                                    const sev = (log.severity || 'info').toLowerCase();
                                    const isCritical = sev === 'critical' || sev === 'high' || sev === 'danger';
                                    const isWarning = sev === 'warning' || sev === 'medium' || sev === 'warn';
                                    return (
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '999px',
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.03em',
                                        background: isCritical ? 'rgba(239, 68, 68, 0.15)' : isWarning ? 'rgba(245, 158, 11, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                                        color: isCritical ? '#ef4444' : isWarning ? '#f59e0b' : '#3b82f6',
                                        border: `1px solid ${isCritical ? 'rgba(239, 68, 68, 0.3)' : isWarning ? 'rgba(245, 158, 11, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
                                      }}>
                                        <span style={{
                                          width: '6px',
                                          height: '6px',
                                          borderRadius: '50%',
                                          background: isCritical ? '#ef4444' : isWarning ? '#f59e0b' : '#3b82f6'
                                        }} />
                                        {(log.severity || 'info').toUpperCase()}
                                      </span>
                                    );
                                  })()}
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                      {(() => {
                        const rawList = auditLogs || [];
                        const filteredList = rawList.filter(log => {
                          if (auditFilterSeverity) {
                            const logSev = (log.severity || '').toLowerCase();
                            const targetSev = auditFilterSeverity.toLowerCase();
                            if (targetSev === 'critical' && !(logSev === 'critical' || logSev === 'high' || logSev === 'danger')) return false;
                            if (targetSev === 'warning' && !(logSev === 'warning' || logSev === 'medium' || logSev === 'warn')) return false;
                            if (targetSev === 'info' && !(logSev === 'info' || logSev === 'low')) return false;
                            if (targetSev !== 'critical' && targetSev !== 'warning' && targetSev !== 'info' && logSev !== targetSev) return false;
                          }

                          if (auditFilterAction) {
                            if ((log.action || '').toLowerCase() !== auditFilterAction.toLowerCase()) return false;
                          }

                          if (auditFilterDate && log.timestamp) {
                            const logDateStr = new Date(log.timestamp).toISOString().split('T')[0];
                            if (logDateStr !== auditFilterDate) return false;
                          }

                          if (auditSearchQuery) {
                            const q = auditSearchQuery.toLowerCase().trim();
                            const actor = (log.actorUsername || log.username || '').toLowerCase();
                            const ip = (log.ipAddress || '').toLowerCase();
                            const action = (log.action || '').toLowerCase();
                            const details = log.details ? (typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details)).toLowerCase() : '';
                            if (!actor.includes(q) && !ip.includes(q) && !action.includes(q) && !details.includes(q)) return false;
                          }

                          return true;
                        });

                        return renderPaginationControls(
                          auditPage,
                          Math.ceil(filteredList.length / saItemsPerPage) || 1,
                          filteredList.length,
                          saItemsPerPage,
                          setAuditPage,
                          "logs"
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'subscriptions' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <h2><CreditCard size={24} strokeWidth={2.0} /> Subscriptions & Billing</h2>
                    <p className="subtitle">Overview of platform revenue and active subscriptions</p>
                  </div>
                  {subscriptions ? (
                    subscriptions.error ? (
                      <div className="sa-loading" style={{ color: 'var(--danger)', flexDirection: 'column', gap: '0.5rem' }}>
                        <AlertCircle size={32} />
                        <p>Failed to load subscription data. The API request was rejected.</p>
                      </div>
                    ) : (
                      <>
                        <div className="sa-premium-stats-grid">
                          <div className="sa-premium-stat-card">
                            <div className="sa-premium-stat-icon active-users"><Check size={24} /></div>
                            <div className="sa-premium-stat-details">
                              <span className="sa-premium-stat-value">{subscriptions.totalActive}</span>
                              <span className="sa-premium-stat-label">Active Subscriptions</span>
                            </div>
                          </div>
                          <div className="sa-premium-stat-card">
                            <div className="sa-premium-stat-icon inactive-users"><User size={24} /></div>
                            <div className="sa-premium-stat-details">
                              <span className="sa-premium-stat-value">{subscriptions.totalInactive}</span>
                              <span className="sa-premium-stat-label">Free/Inactive Users</span>
                            </div>
                          </div>
                        </div>

                        <div className="sa-premium-table-card" style={{ marginTop: '2rem' }}>
                          <div className="sa-premium-table-header">
                            <h3>Active Subscribers</h3>
                          </div>
                          <div className="table-responsive">
                            <table className="sa-table">
                              <thead>
                                <tr>
                                  <th>Transaction ID</th>
                                  <th>User / Company</th>
                                  <th>Amount</th>
                                  <th>Subscription Status</th>
                                  <th>Next Billing Date</th>
                                  <th>Payment Method</th>
                                  <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subscriptions.activeSubscriptions.length === 0 ? (
                                  <tr><td colSpan="7" className="text-center" style={{ padding: '2rem', color: 'var(--text-muted)' }}>No active subscribers</td></tr>
                                ) : (
                                  subscriptions.activeSubscriptions.map((s, idx) => {
                                    // Fallback to real dynamic API data
                                    const txnId = s.transactionId || (s.stripeSubscriptionId ? `TXN-${s.stripeSubscriptionId.slice(-6).toUpperCase()}` : 'N/A');
                                    const amount = s.amount || (s.stripeSubscriptionId ? '$49.00 / mo' : '$0.00 / mo');
                                    const nextBillingStr = s.nextBillingDate ? new Date(s.nextBillingDate).toLocaleDateString() : 'N/A';
                                    const payMethod = s.paymentMethod || (s.stripeSubscriptionId ? 'Credit Card' : 'No card on file');

                                    return (
                                      <tr key={idx}>
                                        <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{txnId}</td>
                                        <td>
                                          <div className="sa-avatar-wrapper">
                                            <div className="sa-user-avatar" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontSize: '0.75rem', width: '28px', height: '28px' }}>{(s.username || 'U').substring(0, 2).toUpperCase()}</div>
                                            <div className="sa-user-info">
                                              <span className="sa-user-name">{s.username || 'Unknown'}</span>
                                              <span className="sa-user-email">{s.email || 'No email'}</span>
                                            </div>
                                          </div>
                                        </td>
                                        <td style={{ fontWeight: 600, color: 'var(--text-main)' }}>{amount}</td>
                                        <td>
                                          <span className={s.stripeSubscriptionId ? "sa-badge-pro" : "sa-badge-free"}>
                                            {s.stripeSubscriptionId ? 'Active / Pro' : 'Free Tier'}
                                          </span>
                                        </td>
                                        <td className="sa-cell-muted">{nextBillingStr}</td>
                                        <td className="sa-cell-muted">{payMethod}</td>
                                        <td style={{ textAlign: "right" }}>
                                          <button className="sa-premium-btn-secondary" style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }} onClick={() => { setEditingLimitsForUser(s); setCustomLimits(s.customLimits || { maxDeployments: '', memoryLimitMB: '', storageLimitGB: '', cpuLimit: '', maxDomains: '', maxDatabases: '', bandwidthLimitGB: '' }); }}>
                                            Manage Resources
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                      </>
                    )
                  ) : (
                    <div className="sa-loading"><Loader2 className="spinning" size={24} /> Loading subscription data...</div>
                  )}
                </div>
              )}
              {activeSection === 'active_subscribers_detail' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <div>
                      <h2><Users size={24} strokeWidth={2.0} /> Active Subscribers</h2>
                      <p className="subtitle">Detailed metrics, subscription history and member analytics</p>
                    </div>
                    <button className="dashboard-refresh-btn" onClick={() => setActiveSection('overview')}>
                      Back to Overview
                    </button>
                  </div>

                  <div className="analytics-detail-container">

                    {/* Summary Quick Stats Cards */}
                    <div className="analytics-summary-cards">
                      <div className="analytics-summary-card">
                        <div className="analytics-summary-icon" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
                          <Users size={22} />
                        </div>
                        <div>
                          <div className="analytics-summary-value">{effectiveSubscribersList.length}</div>
                          <div className="analytics-summary-label">Total Subscribers</div>
                        </div>
                      </div>

                      <div className="analytics-summary-card">
                        <div className="analytics-summary-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                          <Shield size={22} />
                        </div>
                        <div>
                          <div className="analytics-summary-value">{(effectiveSubscribersList || []).filter(u => u.plan === 'Pro').length}</div>
                          <div className="analytics-summary-label">Pro Members</div>
                        </div>
                      </div>

                      <div className="analytics-summary-card">
                        <div className="analytics-summary-icon" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
                          <Activity size={22} />
                        </div>
                        <div>
                          <div className="analytics-summary-value" style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                            {(effectiveSubscribersList || []).filter(u => (u.status || '').toLowerCase() === 'active').length}
                            <span style={{ fontSize: '0.9rem', color: '#10b981', fontWeight: 600 }}>
                              ({effectiveSubscribersList.length > 0 ? Math.round(((effectiveSubscribersList || []).filter(u => (u.status || '').toLowerCase() === 'active').length / effectiveSubscribersList.length) * 100) : 0}%)
                            </span>
                          </div>
                          <div className="analytics-summary-label">Paid Subscribers</div>
                        </div>
                      </div>

                      <div className="analytics-summary-card">
                        <div className="analytics-summary-icon" style={{ background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}>
                          <Users size={22} />
                        </div>
                        <div>
                          <div className="analytics-summary-value" style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                            {(effectiveSubscribersList || []).filter(u => (u.status || '').toLowerCase() !== 'active').length}
                            <span style={{ fontSize: '0.9rem', color: '#ef4444', fontWeight: 600 }}>
                              ({effectiveSubscribersList.length > 0 ? Math.round(((effectiveSubscribersList || []).filter(u => (u.status || '').toLowerCase() !== 'active').length / effectiveSubscribersList.length) * 100) : 0}%)
                            </span>
                          </div>
                          <div className="analytics-summary-label">Unpaid Subscribers</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                      <button
                        onClick={() => setShowSubscriberGraphs(!showSubscriberGraphs)}
                        style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '0.4rem 0.8rem', borderRadius: '6px', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}
                      >
                        {showSubscriberGraphs ? 'Hide Graphs' : 'Show Graphs'}
                        {showSubscriberGraphs ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </div>

                    {showSubscriberGraphs && (
                      <div className="analytics-charts-grid">
                        <div className="analytics-chart-card">
                          <h4 className="analytics-chart-title">Subscriber Growth</h4>
                          <div style={{ height: 250, width: '100%' }}>
                            <ResponsiveContainer>
                              <LineChart data={effectiveChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                                <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                                <Line type="monotone" dataKey="subscribers" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        <div className="analytics-chart-card">
                          <h4 className="analytics-chart-title">Subscribers by Plan</h4>
                          <div style={{ height: 250, width: '100%' }}>
                            <ResponsiveContainer>
                              <PieChart>
                                <Pie
                                  data={[
                                    { name: 'Pro', value: (effectiveSubscribersList || []).filter(u => u.plan === 'Pro').length || 1 },
                                    { name: 'Hobby', value: (effectiveSubscribersList || []).filter(u => u.plan === 'Hobby').length || 1 }
                                  ]}
                                  innerRadius={60}
                                  outerRadius={80}
                                  paddingAngle={5}
                                  dataKey="value"
                                >
                                  <Cell fill="#3b82f6" />
                                  <Cell fill="#10b981" />
                                </Pie>
                                <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                                <Legend verticalAlign="bottom" height={36} iconType="circle" />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="analytics-table-header">
                        <h3 className="analytics-table-title">
                          <Users size={18} color="#10b981" />
                          Subscriber Details
                        </h3>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          <div className="analytics-search-box">
                            <Search size={14} />
                            <input
                              type="text"
                              className="analytics-search-input"
                              placeholder="Search subscribers..."
                              value={subscribersSearch}
                              onChange={(e) => { setSubscribersSearch(e.target.value); setSubscribersPage(1); }}
                            />
                          </div>
                          <div className="analytics-export-dropdown-wrapper">
                            <button className="analytics-export-btn" onClick={() => setShowSubscribersExportMenu(!showSubscribersExportMenu)}>
                              <Download size={14} /> Export Report <ChevronDown size={14} />
                            </button>
                            {showSubscribersExportMenu && (
                              <div className="analytics-export-menu">
                                <button className="analytics-export-menu-item" onClick={() => { handleExportSubscribersCSV(); setShowSubscribersExportMenu(false); }}>
                                  <FileText size={14} /> Export as CSV
                                </button>
                                <button className="analytics-export-menu-item" onClick={() => { handleExportSubscribersPDF(); setShowSubscribersExportMenu(false); }}>
                                  <Printer size={14} /> Export as PDF
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="analytics-table-wrapper">
                        <table className="analytics-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Duration</th>
                              <th>Next Billing Date</th>
                              <th>Type</th>
                              <th>Plan</th>
                              <th>Team Size</th>
                              <th style={{ textAlign: 'right' }}>Status</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedSubscribers.length > 0 ? (
                              paginatedSubscribers.map((u, i) => (
                                <tr key={i}>
                                  <td style={{ color: 'var(--text-main)', fontWeight: 600 }}>{u.name}</td>
                                  <td style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{u.email}</td>
                                  <td style={{ color: 'var(--text-main)', fontSize: '0.9rem' }}>{u.duration}</td>
                                  <td style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{u.planExpiration}</td>
                                  <td style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{u.userType}</td>
                                  <td>
                                    <span className={`analytics-badge ${u.plan === 'Pro' ? 'analytics-badge-pro' : 'analytics-badge-hobby'}`}>
                                      {u.plan}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <span style={{ color: u.isOverQuota ? '#ef4444' : 'var(--text-main)', fontWeight: u.isOverQuota ? 600 : 400 }}>
                                        {u.teamSize} / {u.maxTeamMembers}
                                      </span>
                                      {u.isOverQuota && (
                                        <div title="Quota Exceeded" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '50%', width: '20px', height: '20px' }}>
                                          <AlertCircle size={12} strokeWidth={2.5} />
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <span className={`analytics-status-pill ${u.status === 'active' ? 'analytics-status-active' : u.status === 'partial' ? 'analytics-status-partial' : 'analytics-status-unpaid'}`}>
                                      {u.status === 'active' ? 'PAID' : 'UNPAID'}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    {u.isOverQuota ? (
                                      <button
                                        onClick={() => alert(`Suspended excess members for ${u.name}`)}
                                        style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer' }}
                                      >
                                        Suspend Excess
                                      </button>
                                    ) : (
                                      <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>-</span>
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                                  No subscribers match your search criteria.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination Controls */}
                      <div className="analytics-pagination-footer">
                        <div className="analytics-pagination-info">
                          Showing {filteredSubscribers.length === 0 ? 0 : (subscribersPage - 1) * pageSize + 1} to {Math.min(subscribersPage * pageSize, filteredSubscribers.length)} of {filteredSubscribers.length} entries
                        </div>
                        <div className="analytics-pagination-controls">
                          <button
                            className="analytics-page-btn"
                            disabled={subscribersPage === 1}
                            onClick={() => setSubscribersPage(prev => Math.max(1, prev - 1))}
                          >
                            <ChevronLeft size={16} />
                          </button>
                          {Array.from({ length: totalSubscribersPages }, (_, i) => i + 1).map(p => (
                            <button
                              key={p}
                              className={`analytics-page-btn ${subscribersPage === p ? 'active' : ''}`}
                              onClick={() => setSubscribersPage(p)}
                            >
                              {p}
                            </button>
                          ))}
                          <button
                            className="analytics-page-btn"
                            disabled={subscribersPage === totalSubscribersPages}
                            onClick={() => setSubscribersPage(prev => Math.min(totalSubscribersPages, prev + 1))}
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'churn_details' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <div>
                      <h2><AlertCircle size={24} color="#ef4444" strokeWidth={2.0} /> Customer Churn Analytics</h2>
                      <p className="subtitle">Detailed breakdown of churned users, lost revenue, and retention</p>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <select
                        value={revenueTimeRange}
                        onChange={e => setRevenueTimeRange(e.target.value)}
                        className="settings-input"
                        style={{ margin: 0, padding: '0.4rem 1rem', width: 'auto' }}
                      >
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="this_month">This Month</option>
                        <option value="last_month">Last Month</option>
                        <option value="ytd">Year to Date</option>
                        <option value="all">All Time</option>
                      </select>
                      <button className="dashboard-refresh-btn" onClick={loadAll}>
                        <RefreshCw size={14} /> Refresh
                      </button>
                    </div>
                  </div>

                  {/* Hero Metrics */}
                  <div style={{ padding: '0 2rem', marginBottom: '2rem' }}>
                    <div className="sa-stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                      <div className="sa-stat-card">
                        <div className="sa-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                          <AlertCircle size={24} />
                        </div>
                        <div className="sa-stat-info">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span className="sa-stat-value">{financialStats?.churnRate || 0}%</span>
                            {(financialStats?.churnRate || 0) > 5 ? (
                              <span style={{ fontSize: '0.75rem', color: '#ef4444', display: 'flex', alignItems: 'center' }}><TrendingUp size={12} /> High</span>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#10b981', display: 'flex', alignItems: 'center' }}><TrendingDown size={12} /> Healthy</span>
                            )}
                          </div>
                          <span className="sa-stat-label">Current Churn Rate</span>
                        </div>
                      </div>

                      <div className="sa-stat-card">
                        <div className="sa-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                          <CreditCard size={24} />
                        </div>
                        <div className="sa-stat-info">
                          <span className="sa-stat-value">${Number(financialStats?.churnedRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          <span className="sa-stat-label">Lost MRR (Current Period)</span>
                        </div>
                      </div>

                      <div className="sa-stat-card">
                        <div className="sa-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                          <UserMinus size={24} />
                        </div>
                        <div className="sa-stat-info">
                          <span className="sa-stat-value">{financialStats?.voluntaryChurnRate || 0}% <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 400 }}>Vol.</span></span>
                          <span className="sa-stat-label">Voluntary vs Involuntary</span>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>Inv: {financialStats?.involuntaryChurnRate || 0}%</div>
                        </div>
                      </div>

                      <div className="sa-stat-card">
                        <div className="sa-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                          <ShieldCheck size={24} />
                        </div>
                        <div className="sa-stat-info">
                          <span className="sa-stat-value">{financialStats?.nrr || 100}%</span>
                          <span className="sa-stat-label">Net Retention Rate</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Charts */}
                  <div style={{ padding: '0 2rem', marginBottom: '2rem', display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
                    <div style={{ padding: '1.5rem', background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                      <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: 'var(--text-main)' }}>Churn Rate Trend</h3>
                      <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                          <AreaChart data={financialStats?.chartData || []} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="colorChurn" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                            <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `${val}%`} />
                            <Tooltip
                              contentStyle={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)', borderRadius: '8px' }}
                              itemStyle={{ color: '#ef4444', fontWeight: 600 }}
                            />
                            <Area type="monotone" dataKey="churn" stroke="#ef4444" strokeWidth={3} fillOpacity={1} fill="url(#colorChurn)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div style={{ padding: '1.5rem', background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                      <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: 'var(--text-main)' }}>Churn Reasons</h3>
                      <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={financialStats?.churnReasons || []}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              paddingAngle={5}
                              dataKey="value"
                            >
                              {(financialStats?.churnReasons || []).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={['#3b82f6', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6'][index % 5]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)', borderRadius: '8px' }} />
                            <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '0.8rem', color: 'var(--text-muted)' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Tables */}
                  <div style={{ padding: '0 2rem', marginBottom: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    {/* At-Risk Users */}
                    <div style={{ padding: '1.5rem', background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AlertCircle size={18} color="#f59e0b" />
                        At-Risk Users (Action Required)
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                        {users.filter(u => u.subscriptionStatus === 'past_due' || u.subscriptionStatus === 'unpaid').map(u => (
                          <div key={u.username} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <User size={20} color="#f59e0b" />
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{u.username}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{u.email}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                              <span style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', borderRadius: '4px', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', fontWeight: 600 }}>PAYMENT FAILED</span>
                              <button className="sa-btn-link" style={{ fontSize: '0.75rem', padding: 0 }} onClick={() => startEditUser(u)}>Contact User</button>
                            </div>
                          </div>
                        ))}
                        {users.filter(u => u.subscriptionStatus === 'past_due' || u.subscriptionStatus === 'unpaid').length === 0 && (
                          <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', padding: '2rem 1rem', textAlign: 'center' }}>No at-risk users found.</div>
                        )}
                      </div>
                    </div>

                    {/* Recently Churned Log */}
                    <div style={{ padding: '1.5rem', background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <UserMinus size={18} color="#ef4444" />
                        Recently Churned Log
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                        {users.filter(u => u.subscriptionStatus === 'canceled').map(u => (
                          <div key={u.username} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <User size={20} color="#ef4444" />
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{u.username}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{u.email}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                              <span style={{ fontWeight: 700, color: '#ef4444' }}>-$20.00 <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-muted)' }}>MRR</span></span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Voluntary</span>
                            </div>
                          </div>
                        ))}
                        {users.filter(u => u.subscriptionStatus === 'canceled').length === 0 && (
                          <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', padding: '2rem 1rem', textAlign: 'center' }}>No recently churned users found.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeSection === 'mrr_details' && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <div>
                      <h2><BarChart3 size={24} strokeWidth={2.0} /> General Revenue Analytics</h2>
                      <p className="subtitle">Detailed breakdown of revenue, MRR, churn, and subscriptions</p>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <select
                        value={revenueTimeRange}
                        onChange={e => setRevenueTimeRange(e.target.value)}
                        className="settings-input"
                        style={{ margin: 0, padding: '0.4rem 1rem', width: 'auto' }}
                      >
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="this_month">This Month</option>
                        <option value="last_month">Last Month</option>
                        <option value="ytd">Year to Date</option>
                        <option value="all">All Time</option>
                      </select>
                      <select
                        value={revenueTypeFilter}
                        onChange={e => setRevenueTypeFilter(e.target.value)}
                        className="settings-input"
                        style={{ margin: 0, padding: '0.4rem 1rem', width: 'auto' }}
                      >
                        <option value="all">All Revenue</option>
                        <option value="mrr">Recurring (MRR) Only</option>
                        <option value="one-time">One-Time Only</option>
                      </select>
                      <button className="dashboard-refresh-btn" onClick={() => setActiveSection('overview')}>
                        Back to Overview
                      </button>
                    </div>
                  </div>

                  <div className="analytics-detail-container">

                    {/* Hero Metric & Sub-Metrics */}
                    <div className="analytics-summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                      {/* Hero Metric */}
                      <div className="analytics-summary-card" style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div className="analytics-summary-label" style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.5rem' }}>Total Revenue</div>
                            <div className="analytics-summary-value" style={{ fontSize: '2.5rem', color: '#2563eb' }}>${Number(financialStats?.totalRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </div>
                          <div className="analytics-summary-icon" style={{ background: '#3b82f6', color: '#fff', width: '48px', height: '48px' }}>
                            <CreditCard size={28} />
                          </div>
                        </div>
                        {financialStats?.prevTotalRevenue !== undefined && (
                          <div style={{ marginTop: '1rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {financialStats.totalRevenue >= financialStats.prevTotalRevenue ? (
                              <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', fontWeight: 600 }}><ChevronRight style={{ transform: 'rotate(-45deg)' }} size={16} /> +{financialStats.prevTotalRevenue ? (((financialStats.totalRevenue - financialStats.prevTotalRevenue) / financialStats.prevTotalRevenue) * 100).toFixed(1) : 100}%</span>
                            ) : (
                              <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', fontWeight: 600 }}><ChevronRight style={{ transform: 'rotate(45deg)' }} size={16} /> {financialStats.prevTotalRevenue ? (((financialStats.totalRevenue - financialStats.prevTotalRevenue) / financialStats.prevTotalRevenue) * 100).toFixed(1) : 0}%</span>
                            )}
                            <span style={{ color: 'var(--text-muted)' }}>vs previous period</span>
                          </div>
                        )}
                      </div>

                      <div className="analytics-summary-card">
                        <div className="analytics-summary-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                          <Activity size={22} />
                        </div>
                        <div>
                          <div className="analytics-summary-value">${Number(financialStats?.estimatedMRR || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="analytics-summary-label">Current MRR</div>
                        </div>
                      </div>

                      <div className="analytics-summary-card">
                        <div className="analytics-summary-icon" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
                          <Users size={22} />
                        </div>
                        <div>
                          <div className="analytics-summary-value">${Number(financialStats?.arpu || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="analytics-summary-label">ARPU</div>
                        </div>
                      </div>

                      <div className="analytics-summary-card">
                        <div className="analytics-summary-icon" style={{ background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}>
                          <AlertCircle size={22} />
                        </div>
                        <div>
                          <div className="analytics-summary-value">{financialStats?.churnRate || 0}%</div>
                          <div className="analytics-summary-label">Churn Rate</div>
                        </div>
                      </div>

                      <div className="analytics-summary-card">
                        <div className="analytics-summary-icon" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
                          <TrendingUp size={22} />
                        </div>
                        <div>
                          <div className="analytics-summary-value">{financialStats?.nrr || 100}%</div>
                          <div className="analytics-summary-label">Net Rev. Retention</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
                      <div className="analytics-chart-card">
                        <h4 className="analytics-chart-title">Revenue Growth Trend</h4>
                        <div style={{ height: 300, width: '100%' }}>
                          <ResponsiveContainer>
                            <AreaChart data={financialStats?.chartData || []}>
                              <defs>
                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                              <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                              <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val}`} />
                              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px' }} formatter={(val) => [`$${val}`, 'Revenue']} />
                              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="analytics-chart-card">
                        <h4 className="analytics-chart-title">Revenue Composition</h4>
                        <div style={{ height: 300, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <ResponsiveContainer width="100%" height="80%">
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'MRR', value: financialStats?.revenueComposition?.mrr || 0 },
                                  { name: 'One-Time', value: financialStats?.revenueComposition?.oneTime || 0 }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                              >
                                <Cell key="cell-mrr" fill="#3b82f6" />
                                <Cell key="cell-onetime" fill="#10b981" />
                              </Pie>
                              <Tooltip formatter={(val) => [`$${val.toFixed(2)}`, 'Amount']} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#3b82f6' }} /> Recurring
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#10b981' }} /> One-Time
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                      {/* Top Customers (if available) */}
                      {financialStats?.topCustomers && financialStats.topCustomers.length > 0 && (
                        <div className="analytics-table-wrapper" style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '1.5rem' }}>
                          <h3 className="analytics-table-title" style={{ marginBottom: '1rem' }}><Users size={18} color="#f59e0b" /> Top Customers by Revenue</h3>
                          <table className="analytics-table">
                            <thead>
                              <tr>
                                <th>Customer Name</th>
                                <th>Email</th>
                                <th style={{ textAlign: 'right' }}>Total Contributed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {financialStats.topCustomers.map((cust, i) => (
                                <tr key={i}>
                                  <td style={{ fontWeight: 600, color: 'var(--text-main)' }}>{cust.name}</td>
                                  <td style={{ color: 'var(--text-muted)' }}>{cust.email}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600, color: '#10b981' }}>${Number(cust.amount).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Transactions Table */}
                      <div className="analytics-table-wrapper" style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '0' }}>
                        <div className="analytics-table-header" style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', margin: 0 }}>
                          <h3 className="analytics-table-title" style={{ margin: 0 }}>
                            <CreditCard size={18} color="#3b82f6" />
                            Recent Transactions
                          </h3>
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <div className="analytics-search-box">
                              <Search size={14} />
                              <input
                                type="text"
                                className="analytics-search-input"
                                placeholder="Search transactions..."
                                value={transactionsSearch}
                                onChange={(e) => { setTransactionsSearch(e.target.value); setTransactionsPage(1); }}
                              />
                            </div>
                            <div className="analytics-export-dropdown-wrapper">
                              <button className="analytics-export-btn" onClick={() => setShowMrrExportMenu(!showMrrExportMenu)}>
                                <Download size={14} /> Export Report <ChevronDown size={14} />
                              </button>
                              {showMrrExportMenu && (
                                <div className="analytics-export-menu">
                                  <button className="analytics-export-menu-item" onClick={() => { handleExportTransactionsCSV(); setShowMrrExportMenu(false); }}>
                                    <FileText size={14} /> Export as CSV
                                  </button>
                                  <button className="analytics-export-menu-item" onClick={() => { handleExportTransactionsPDF(); setShowMrrExportMenu(false); }}>
                                    <Printer size={14} /> Export as PDF
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                          <table className="analytics-table">
                            <thead>
                              <tr>
                                <th>Transaction ID</th>
                                <th>Customer</th>
                                <th>Email</th>
                                <th>Service/Plan</th>
                                <th>Amount</th>
                                <th>Date</th>
                                <th>Next Billing Date</th>
                                <th style={{ textAlign: 'right' }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedTransactions.length > 0 ? (
                                paginatedTransactions.map((txn, i) => (
                                  <tr key={txn.id || i}>
                                    <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontFamily: 'monospace' }}>{txn.transId}</td>
                                    <td style={{ color: 'var(--text-main)', fontWeight: 600 }}>{txn.username}</td>
                                    <td style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{txn.email}</td>
                                    <td>
                                      <span className={`analytics-badge ${txn.plan === 'Pro' ? 'analytics-badge-pro' : 'analytics-badge-hobby'}`}>
                                        {txn.plan}
                                      </span>
                                    </td>
                                    <td style={{ color: 'var(--text-main)', fontWeight: 600 }}>{txn.price}</td>
                                    <td style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{txn.date}</td>
                                    <td style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                      {txn.nextBillingDate || (txn.date ? new Date(new Date(txn.date).getTime() + 30 * 86400000).toLocaleDateString() : 'N/A')}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                      <span className={`analytics-status-pill ${txn.status === 'Paid' || txn.status === 'COMPLETED' ? 'analytics-status-active' : 'analytics-status-unpaid'}`}>
                                        {(txn.status || 'Paid').toUpperCase()}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                                    No transactions match your search criteria.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination Controls */}
                        <div className="analytics-pagination-footer" style={{ borderTop: '1px solid var(--border)', padding: '1rem 1.5rem', margin: 0 }}>
                          <div className="analytics-pagination-info">
                            Showing {filteredTransactions.length === 0 ? 0 : (transactionsPage - 1) * pageSize + 1} to {Math.min(transactionsPage * pageSize, filteredTransactions.length)} of {filteredTransactions.length} entries
                          </div>
                          <div className="analytics-pagination-controls">
                            <button
                              className="analytics-page-btn"
                              disabled={transactionsPage === 1}
                              onClick={() => setTransactionsPage(prev => Math.max(1, prev - 1))}
                            >
                              <ChevronLeft size={16} />
                            </button>
                            {Array.from({ length: totalTransactionsPages }, (_, i) => i + 1).map(p => (
                              <button
                                key={p}
                                className={`analytics-page-btn ${transactionsPage === p ? 'active' : ''}`}
                                onClick={() => setTransactionsPage(p)}
                              >
                                {p}
                              </button>
                            ))}
                            <button
                              className="analytics-page-btn"
                              disabled={transactionsPage === totalTransactionsPages}
                              onClick={() => setTransactionsPage(prev => Math.min(totalTransactionsPages, prev + 1))}
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Upgrade Plan Modal */}
              {managingPlanUser && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="sa-card" style={{ width: '450px', maxWidth: '90%', margin: 0 }}>
                    <div className="sa-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Upgrade Plan: {managingPlanUser.username}</h3>
                      <button className="active-deployment-dismiss" onClick={() => setManagingPlanUser(null)}><X size={20} /></button>
                    </div>
                    <form onSubmit={handleUpgradePlan} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      <div className="form-group">
                        <label>Select Premium Plan</label>
                        <select className="premium-input" required>
                          <option value="pro">Pro Plan ($20/mo)</option>
                          <option value="enterprise">Enterprise Custom</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <button type="button" className="action-btn-premium" onClick={() => setManagingPlanUser(null)}>Cancel</button>
                        <button type="submit" className="action-btn-premium primary" disabled={planUpgradeLoading}>
                          {planUpgradeLoading ? 'Processing...' : 'Force Upgrade'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Invoices Drawer Modal */}
              {viewingInvoicesUser && (
                <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '450px', background: 'var(--surface)', borderLeft: '1px solid var(--border)', zIndex: 1000, padding: '2rem', boxShadow: '-10px 0 20px rgba(0,0,0,0.1)', overflowY: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Billing History</h2>
                    <button className="active-deployment-dismiss" onClick={() => setViewingInvoicesUser(null)}><X size={20} /></button>
                  </div>
                  <div style={{ marginBottom: '1.5rem', color: 'var(--text-muted)' }}>Invoices for <strong>{viewingInvoicesUser}</strong></div>

                  {isInvoicesLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}><RefreshCw className="spin" size={24} color="var(--text-muted)" /></div>
                  ) : invoicesData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No invoices found.</div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {(() => {
                          const paginatedInv = invoicesData.slice((invoicesPage - 1) * saItemsPerPage, invoicesPage * saItemsPerPage);
                          return paginatedInv.map((inv, i) => (
                            <div key={i} style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.25rem' }}>{(inv.amount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{new Date(inv.date).toLocaleDateString()} • {inv.id}</div>
                              </div>
                              <span style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', textTransform: 'capitalize' }}>
                                {inv.status}
                              </span>
                            </div>
                          ));
                        })()}
                      </div>
                      {renderPaginationControls(invoicesPage, Math.ceil((invoicesData || []).length / saItemsPerPage) || 1, (invoicesData || []).length, saItemsPerPage, setInvoicesPage, "invoices")}
                    </div>
                  )}
                </div>
              )}
              {/* User Allocations Section */}
              {activeSection === 'user_allocations' && selectedUserResourceDetails && (
                <div className="sa-section fade-in">
                  <div className="sa-section-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <button className="sa-action-btn" style={{ margin: 0, color: 'var(--text-main)', borderColor: 'var(--border)' }} onClick={() => setActiveSection('users')}>
                        <ChevronLeft size={20} />
                      </button>
                      <div>
                        <h2 style={{ margin: 0 }}>{selectedUserResourceDetails.username}'s Resource Allocations</h2>
                        <p className="subtitle">Detailed breakdown of active containers, RAM, and CPU usage.</p>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const usage = userResourceUsage?.find(u => u.username === selectedUserResourceDetails.username);
                    const deployments = usage?.deployments || [];
                    const activeApps = usage?.activeApps || 0;
                    const planLimits = usage?.planLimits;

                    // Is this an unlimited admin user?
                    const isUnlimitedAdmin = ['superadmin', 'system_admin', 'systemadmin'].includes(selectedUserResourceDetails.role);

                    // Tier defaults matching server's plan-limits.js
                    const TIER_DEFAULTS = {
                      hobby: { maxDeployments: 3, maxTotalMemory: '1536m', maxMemoryPerContainer: '512m', maxStorageGB: 1 },
                      pro: { maxDeployments: 25, maxTotalMemory: '10g', maxMemoryPerContainer: '2g', maxStorageGB: 10 },
                      enterprise: { maxDeployments: 100, maxTotalMemory: '32g', maxMemoryPerContainer: '8g', maxStorageGB: 50 },
                    };
                    const ROLE_TO_TIER = { developer: 'hobby', admin: 'pro', superadmin: 'enterprise' };
                    const userTier = selectedUserResourceDetails.packageTier
                      || ROLE_TO_TIER[selectedUserResourceDetails.role]
                      || 'hobby';

                    const pkg = packages?.find(p => p.tierId === userTier);
                    const tierDefaults = TIER_DEFAULTS[userTier] || TIER_DEFAULTS.hobby;

                    const resolveLimit = (...args) => {
                      for (const arg of args) {
                        if (arg !== null && arg !== undefined && arg !== '') {
                          return arg;
                        }
                      }
                      return undefined;
                    };

                    // Deployments
                    const maxDeployments = isUnlimitedAdmin
                      ? Infinity
                      : Number(resolveLimit(
                        selectedUserResourceDetails.customLimits?.maxDeployments,
                        planLimits?.maxDeployments,
                        pkg?.maxDeployments,
                        tierDefaults.maxDeployments
                      ));
                    const deployPercent = isUnlimitedAdmin ? 0 : Math.min(100, Math.round((activeApps / maxDeployments) * 100));

                    // Storage (not tracked per user on disk yet)
                    const storageUsed = 0;
                    const maxStorageGB = isUnlimitedAdmin
                      ? Infinity
                      : Number(resolveLimit(
                        selectedUserResourceDetails.customLimits?.storageLimitGB,
                        planLimits?.maxStorageGB,
                        pkg?.maxStorageGB,
                        tierDefaults.maxStorageGB
                      ));
                    const maxStorage = maxStorageGB === Infinity ? Infinity : maxStorageGB * 1024 * 1024 * 1024;
                    const storagePercent = isUnlimitedAdmin ? 0 : Math.min(100, Math.round((storageUsed / maxStorage) * 100));

                    // RAM — respect customLimits first, then planLimits from API, then tier defaults
                    const parseMemStr = (s) => {
                      if (!s) return 0;
                      if (typeof s === 'number') return s;
                      const m = String(s).trim().match(/^([0-9.]+)\s*([a-z]*)$/i);
                      if (!m) return parseInt(s) || 0;
                      const v = parseFloat(m[1]);
                      const u = m[2].toLowerCase();
                      const mul = { b: 1, '': 1, k: 1024, kb: 1024, m: 1024 ** 2, mb: 1024 ** 2, g: 1024 ** 3, gb: 1024 ** 3 };
                      return Math.round(v * (mul[u] ?? 1));
                    };

                    const customRamMB = selectedUserResourceDetails.customLimits?.memoryLimitMB;
                    const resolvedRam = resolveLimit(
                      (customRamMB !== undefined && customRamMB !== null && customRamMB !== '') ? Number(customRamMB) * 1024 * 1024 : undefined,
                      planLimits?.maxTotalMemory ? parseMemStr(planLimits.maxTotalMemory) : undefined,
                      pkg?.maxTotalMemory ? parseMemStr(pkg.maxTotalMemory) : undefined,
                      parseMemStr(tierDefaults.maxTotalMemory)
                    );

                    const ramUsed = usage?.memUsageBytes || 0;
                    const ramLimit = isUnlimitedAdmin ? Infinity : resolvedRam;
                    const ramPercent = isUnlimitedAdmin ? 0 : (ramLimit > 0 ? Math.min(100, Math.round((ramUsed / ramLimit) * 100)) : 0);

                    return (
                      <>
                        <div className="sa-stats-grid">
                          <div className="sa-stat-card" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1.25rem' }}>
                              <div className="sa-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                                <AppWindow size={24} />
                              </div>
                              <div className="sa-stat-info">
                                <span className="sa-stat-value">{activeApps} / {maxDeployments === Infinity ? 'Unlimited' : maxDeployments} Apps</span>
                                <span className="sa-stat-label">Active Deployments</span>
                              </div>
                            </div>
                            <div style={{ marginTop: 'auto' }}>
                              <div className="sa-progress-bg" style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden', width: '100%' }}>
                                <div className="sa-progress-fill" style={{ width: `${deployPercent}%`, height: '100%', background: deployPercent > 85 ? '#ef4444' : '#3b82f6', borderRadius: '3px', transition: 'width 0.5s ease-in-out' }}></div>
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem', textAlign: 'right', fontWeight: 500 }}>{deployPercent}% Used</div>
                            </div>
                          </div>

                          <div className="sa-stat-card" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1.25rem' }}>
                              <div className="sa-stat-icon" style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}>
                                <Database size={24} />
                              </div>
                              <div className="sa-stat-info">
                                <span className="sa-stat-value">{formatBytesValue(storageUsed)}</span>
                                <span className="sa-stat-label">Disk Storage (Max: {maxStorage === Infinity ? 'Unlimited' : formatBytesValue(maxStorage)})</span>
                              </div>
                            </div>
                            <div style={{ marginTop: 'auto' }}>
                              <div className="sa-progress-bg" style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden', width: '100%' }}>
                                <div className="sa-progress-fill" style={{ width: `${storagePercent}%`, height: '100%', background: storagePercent > 85 ? '#ef4444' : '#a855f7', borderRadius: '3px', transition: 'width 0.5s ease-in-out' }}></div>
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem', textAlign: 'right', fontWeight: 500 }}>{storagePercent}% Used</div>
                            </div>
                          </div>

                          <div className="sa-stat-card" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1.25rem' }}>
                              <div className="sa-stat-icon" style={{ background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }}>
                                <Activity size={24} />
                              </div>
                              <div className="sa-stat-info">
                                <span className="sa-stat-value">{formatBytesValue(ramUsed)}</span>
                                <span className="sa-stat-label">RAM (Limit: {ramLimit === Infinity ? 'Unlimited' : (ramLimit > 0 ? formatBytesValue(ramLimit) : 'N/A')})</span>
                              </div>
                            </div>
                            <div style={{ marginTop: 'auto' }}>
                              <div className="sa-progress-bg" style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden', width: '100%' }}>
                                <div className="sa-progress-fill" style={{ width: `${ramPercent}%`, height: '100%', background: ramPercent > 85 ? '#ef4444' : '#ec4899', borderRadius: '3px', transition: 'width 0.5s ease-in-out' }}></div>
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem', textAlign: 'right', fontWeight: 500 }}>{ramPercent}% Used</div>
                            </div>
                          </div>

                          <div className="sa-stat-card" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1.25rem' }}>
                              <div className="sa-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                                <Cpu size={24} />
                              </div>
                              <div className="sa-stat-info">
                                <span className="sa-stat-value">{usage?.cpuPercent?.toFixed(1) || '0.0'}%</span>
                                <span className="sa-stat-label">Total CPU Usage</span>
                              </div>
                            </div>
                            <div style={{ marginTop: 'auto' }}>
                              <div className="sa-progress-bg" style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden', width: '100%' }}>
                                <div className="sa-progress-fill" style={{ width: `${Math.min(100, usage?.cpuPercent || 0)}%`, height: '100%', background: '#ef4444', borderRadius: '3px', transition: 'width 0.5s ease-in-out' }}></div>
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem', textAlign: 'right', fontWeight: 500 }}>Current Load</div>
                            </div>
                          </div>
                        </div>

                        <div className="sa-card">
                          <div className="sa-card-header">
                            <h3>Deployed Applications</h3>
                          </div>
                          <div className="sa-table-wrapper">
                            <table className="sa-table">
                              <thead>
                                <tr>
                                  <th>App Name</th>
                                  <th>Status</th>
                                  <th>CPU %</th>
                                  <th>RAM Usage</th>
                                </tr>
                              </thead>
                              <tbody>
                                {deployments.length === 0 ? (
                                  <tr><td colSpan="4" className="sa-empty-cell">No active deployments.</td></tr>
                                ) : (
                                  deployments.map((dep, i) => (
                                    <tr key={i}>
                                      <td className="sa-cell-bold">{dep.appName}</td>
                                      <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dep.status === 'running' ? '#22c55e' : '#64748b' }}></div>
                                          <span style={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>{dep.status}</span>
                                        </div>
                                      </td>
                                      <td>{dep.cpuPercent.toFixed(2)}%</td>
                                      <td>{formatBytesValue(dep.memUsageBytes)}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

            </div>
          </div>
        </section>
      </main>
      {editingLimitsForUser && (
        <div className="sa-premium-modal-overlay">
          <div className="sa-premium-modal-content">
            <div className="sa-premium-modal-header">
              <h3>Edit Limits: {editingLimitsForUser.username}</h3>
              <button style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.25rem', borderRadius: '4px' }} onClick={() => setEditingLimitsForUser(null)}><X size={20} /></button>
            </div>
            <div className="sa-premium-modal-body">
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>Assign custom resource quotas for this subscription. Leave fields empty to use standard plan defaults.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>Max Deployments (Apps)</label>
                <input type="number" className="premium-input" placeholder="e.g. 10" value={customLimits.maxDeployments || ''} onChange={e => setCustomLimits({ ...customLimits, maxDeployments: e.target.value })} style={{ padding: '0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '8px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>Memory Limit (MB)</label>
                <input type="number" className="premium-input" placeholder="e.g. 2048" value={customLimits.memoryLimitMB || ''} onChange={e => setCustomLimits({ ...customLimits, memoryLimitMB: e.target.value })} style={{ padding: '0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '8px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>Storage Limit (GB)</label>
                <input type="number" className="premium-input" placeholder="e.g. 50" value={customLimits.storageLimitGB || ''} onChange={e => setCustomLimits({ ...customLimits, storageLimitGB: e.target.value })} style={{ padding: '0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '8px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>CPU Limit (vCPUs)</label>
                <input type="number" step="0.5" className="premium-input" placeholder="e.g. 2.0" value={customLimits.cpuLimit || ''} onChange={e => setCustomLimits({ ...customLimits, cpuLimit: e.target.value })} style={{ padding: '0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '8px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>Max Custom Domains</label>
                <input type="number" className="premium-input" placeholder="e.g. 5" value={customLimits.maxDomains || ''} onChange={e => setCustomLimits({ ...customLimits, maxDomains: e.target.value })} style={{ padding: '0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '8px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>Max Databases</label>
                <input type="number" className="premium-input" placeholder="e.g. 2" value={customLimits.maxDatabases || ''} onChange={e => setCustomLimits({ ...customLimits, maxDatabases: e.target.value })} style={{ padding: '0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '8px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>Network Bandwidth (GB/month)</label>
                <input type="number" className="premium-input" placeholder="e.g. 100" value={customLimits.bandwidthLimitGB || ''} onChange={e => setCustomLimits({ ...customLimits, bandwidthLimitGB: e.target.value })} style={{ padding: '0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '8px' }} />
              </div>
            </div>
            <div className="sa-premium-modal-footer">
              <button className="sa-premium-btn-secondary" onClick={() => setEditingLimitsForUser(null)}>Cancel</button>
              <button className="sa-premium-btn" onClick={handleSaveLimits} disabled={isSavingLimits}>
                {isSavingLimits ? 'Saving...' : 'Save Limits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
